import { supabase } from "@/lib/supabase";
import { messageCoordonneesInterdites, texteContientCoordonnees } from "@/lib/contactGuard";
import { offreVenteCorrespondARecherche } from "@/lib/rechercheCriteresMatch";
import { DEFAULT_SELLER_PAYOUT_HOURS, SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import {
  isDisputeOpeningBlockedByPlatform,
  OPEN_DISPUTE_BLOCKED_PLATFORM_DETAIL_FR,
} from "@/lib/disputeEligibility";
import { profileDisplayName } from "@/lib/profileDisplayName";
import {
  dossierStatusLabelFr,
  litigeRowStatusLabelFr,
  negotiationStatusLabelFr,
} from "@/lib/marketplaceStatusLabels";
import { isQualificationProduitNom, sortProduitChampsForDisplay } from "@/lib/produitChampsForm";
import { dossierMatchesMarketplaceSearch } from "@/lib/marketplaceSearch";
import { buildCatalogBroadcastMessage } from "@/lib/catalogBroadcastMessage";
import { getPublicSiteUrl } from "@/config/site";
import { isSubscriptionRowPaidActive } from "@/lib/subscriptionActivation";
import { normalizePhoneFieldsInDossierPayload } from "@/lib/dossierPhoneNormalize";

const nextApiBase = () =>
  (process.env.REACT_APP_NEXT_API_URL || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const API = `${nextApiBase()}/api`;

export function wsUrl(path) {
  const base =
    nextApiBase() ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:3000");
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const p = path.startsWith("/api") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`;
  url.pathname = p;
  return url.toString();
}

export function createHttpError(status, detail, data = {}) {
  const err = new Error(typeof detail === "string" ? detail : "Request failed");
  err.response = { status, data: { detail, ...data } };
  return err;
}

/** Schéma `litiges` : le code attend `status` ; certaines bases n’ont que `statut`. */
function litigeStatusRaw(row) {
  return String(row?.status ?? row?.statut ?? "").trim().toLowerCase();
}

function isLitigeRowClosed(row) {
  const s = litigeStatusRaw(row);
  return s === "closed" || s === "clos" || s === "resolu" || s === "résolu";
}

async function countOpenLitigesForAdminStats() {
  const r1 = await supabase
    .from("litiges")
    .select("id", { count: "exact", head: true })
    .in("status", ["open", "in_review"]);
  if (!r1.error) return Number(r1.count || 0);
  const m1 = String(r1.error?.message || "").toLowerCase();
  if (!m1.includes("status") || !m1.includes("does not exist")) {
    console.warn("[RdvMarket] admin/stats litiges (status):", r1.error?.message);
    return 0;
  }
  const r2 = await supabase
    .from("litiges")
    .select("id", { count: "exact", head: true })
    .in("statut", ["open", "in_review"]);
  if (!r2.error) return Number(r2.count || 0);
  const { data: rows, error: r3 } = await supabase.from("litiges").select("*").limit(2000);
  if (r3) {
    console.warn("[RdvMarket] admin/stats litiges (fallback):", r3.message);
    return 0;
  }
  return (rows || []).filter((l) => {
    const s = litigeStatusRaw(l);
    return s === "open" || s === "in_review";
  }).length;
}

/** Comptage exact (head) pour le bandeau admin — 0 si colonne/table absente. */
async function adminStatsExactCount(queryPromise) {
  const { count, error } = await queryPromise;
  if (error) {
    const m = String(error.message || "").toLowerCase();
    if (
      m.includes("does not exist") ||
      m.includes("column") ||
      (m.includes("relation") && m.includes("does not exist")) ||
      m.includes("schema cache")
    ) {
      return 0;
    }
    console.warn("[RdvMarket] admin/stats:", error.message);
    return 0;
  }
  return Number(count ?? 0);
}

/** Sérialise getSession / getUser : en parallèle (ex. chargement Admin), le client Supabase peut lever « Lock broken by another request with the 'steal' option » (Web Locks). */
let authReadTail = Promise.resolve();
function runAuthLocked(task) {
  const next = authReadTail.then(
    () => task(),
    () => task(),
  );
  authReadTail = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

async function getAccessToken() {
  const { data } = await runAuthLocked(() => supabase.auth.getSession());
  return data.session?.access_token ?? null;
}

function attachCallHandoffHeader(headers, rel, config) {
  // Avec une session Supabase, ne pas envoyer de jeton QR : un handoff expiré dans sessionStorage
  // faisait échouer l’auth avant le Bearer (resolveCallBuyerAuth testait le handoff en premier).
  if (headers.Authorization) return;
  const tid =
    config.handoffTransactionId ||
    extractTransactionIdFromCallsApiPath(rel) ||
    extractTransactionIdFromCallQuery(config.params);
  if (!tid || typeof window === "undefined") return;
  try {
    const tok = window.sessionStorage.getItem(`rdm_call_h_${tid}`);
    if (tok) headers["X-Call-Handoff"] = tok;
  } catch {
    /* ignore */
  }
}

function extractTransactionIdFromCallsApiPath(rel) {
  const m = rel.match(/\/api\/calls\/(?:start|end)\/([^/?]+)/);
  return m ? m[1] : null;
}

function extractTransactionIdFromCallQuery(params) {
  if (!params || typeof params !== "object") return null;
  const tid = params.transactionId ?? params.transaction_id;
  return tid != null ? String(tid) : null;
}

async function nextFetch(method, path, body, config = {}) {
  const base = nextApiBase();
  let rel = path.startsWith("/") ? path : `/${path}`;
  // Sur le domaine du front (base vide), les routes doivent viser /api/... pour Vercel.
  if (!/^https?:\/\//i.test(rel) && !rel.startsWith("/api")) {
    rel = `/api${rel}`;
  }
  const url = base ? `${base.replace(/\/$/, "")}${rel}` : rel;
  const headers = { ...(config.headers || {}) };
  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  attachCallHandoffHeader(headers, rel, config);
  const opts = { method, headers, credentials: "include" };
  const isForm = body instanceof FormData;
  if (body != null && method !== "GET" && method !== "HEAD") {
    if (isForm) {
      opts.body = body;
    } else {
      if (!headers["Content-Type"]) headers["Content-Type"] = "application/json";
      opts.body = typeof body === "string" ? body : JSON.stringify(body);
    }
  }
  const qs = config.params && new URLSearchParams(config.params).toString();
  const finalUrl = qs ? `${url}?${qs}` : url;
  const res = await fetch(finalUrl, opts);
  if (config.responseType === "blob") {
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const t = await res.text();
        try {
          detail = JSON.parse(t).detail || t;
        } catch {
          detail = t || detail;
        }
      } catch {}
      throw createHttpError(res.status, detail);
    }
    return { data: await res.blob(), status: res.status, headers: res.headers };
  }
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { detail: text };
  }
  if (!res.ok) {
    const detail = json?.detail || json?.message || res.statusText;
    throw createHttpError(res.status, detail, json || {});
  }
  return { data: json, status: res.status, headers: res.headers };
}

async function requireUser() {
  const {
    data: { user },
    error,
  } = await runAuthLocked(() => supabase.auth.getUser());
  if (error || !user) throw createHttpError(401, "Non authentifié");
  return user;
}

async function requireAdmin() {
  const user = await requireUser();
  const { data: p, error } = await supabase.from("profiles").select("role").eq("id", user.id).maybeSingle();
  if (error) console.warn("profiles (admin):", error.message);
  if (p?.role !== "admin") throw createHttpError(403, "Accès réservé aux administrateurs");
  return user;
}

/** Options type « list » : tableau, JSON, texte avec retours à la ligne, ou ancien format « a, b ». */
function coalesceProduitChampOptions(options) {
  if (Array.isArray(options)) {
    return options.map((o) => String(o).trim()).filter(Boolean);
  }
  if (options == null) return [];
  if (typeof options === "string") {
    const raw = String(options).trim();
    if (!raw) return [];
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p.map((o) => String(o).trim()).filter(Boolean);
    } catch {
      /* chaîne libre */
    }
    if (/\r?\n/.test(raw)) return raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function mapProduitChampsList(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows
    .slice()
    .sort((a, b) => {
      const oa = a.ordre != null ? Number(a.ordre) : 0;
      const ob = b.ordre != null ? Number(b.ordre) : 0;
      if (oa !== ob) return oa - ob;
      const ida = a.id ?? 0;
      const idb = b.id ?? 0;
      return ida - idb;
    })
    .map((c) => {
      let type = c.type ?? c.champ_type ?? c.field_type ?? "text";
      if (type === "select" || type === "dropdown" || type === "choice" || type === "Liste" || type === "liste") {
        type = "list";
      }
      const known = new Set(["text", "number", "list", "boolean"]);
      if (!known.has(String(type))) type = "text";
      const options = coalesceProduitChampOptions(c.options);
      const cibleAnnonces = c.cible_annonces;
      return {
        champ_id: c.id ?? c.champ_id,
        nom: c.nom ?? c.nom_champ ?? c.name ?? c.libelle ?? c.label,
        type,
        options,
        cible_annonces: cibleAnnonces === "vente" || cibleAnnonces === "recherche" ? cibleAnnonces : "tous",
        required: Boolean(
          c.required ?? c.is_required ?? c.obligatoire ?? c.must_fill
        ),
        /** Si false, saisi en création de dossier mais jamais affiché sur la fiche publique (visible vendeur / admin seulement). */
        visible_public: c.visible_public != null ? Boolean(c.visible_public) : true,
      };
    });
}

function mapProduitRow(row) {
  if (!row) return row;
  let champs = mapProduitChampsList(row.produit_champs || row.champs);
  if (isQualificationProduitNom(row.nom)) {
    champs = sortProduitChampsForDisplay(champs, { produitNom: row.nom });
  }
  return {
    ...row,
    produit_id: row.produit_id ?? row.id,
    champs,
  };
}

function normalizeDossierType(raw) {
  const t = String(raw || "").toLowerCase().trim();
  if (t === "recherche" || t === "achat" || t === "buy") return "recherche";
  if (t === "vente" || t === "offre" || t === "sell") return "vente";
  return t;
}

function mapDossierRow(row, sellerProfile = null) {
  if (!row) return row;
  const seller = row.seller || row.profiles || row.seller_profile;
  const fromProf = sellerProfile ? profileDisplayName(sellerProfile) : "";
  const legacyName = row.seller_name ?? seller?.name ?? seller?.nom ?? null;
  const sellerEmail = sellerProfile?.email ?? row.seller_email ?? seller?.email ?? null;
  const sellerPhone =
    sellerProfile?.phone ?? sellerProfile?.telephone ?? row.seller_phone ?? seller?.phone ?? null;
  const titreRaw = row.titre ?? row.title ?? "";
  const title =
    String(row.title ?? "").trim() ||
    String(titreRaw).trim() ||
    null;
  return {
    ...row,
    dossier_id: row.dossier_id ?? row.id,
    title: title ?? row.title ?? row.titre,
    titre: String(titreRaw || row.titre || title || "").trim() || row.titre,
    type: normalizeDossierType(row.type),
    seller_name: fromProf || legacyName || null,
    seller_email: sellerEmail,
    seller_phone: sellerPhone,
    seller_kyc_status: row.seller_kyc_status ?? seller?.kyc_status ?? null,
  };
}

/** Colonne manquante en base (migrations non appliquées) */
function isMissingDossierColumnError(error, colName) {
  const m = (error?.message || "").toLowerCase();
  const c = (colName || "").toLowerCase();
  if (!c) return false;
  return m.includes(c) && (m.includes("does not exist") || m.includes("column") || m.includes("schema") || m.includes("unknown"));
}

function isPostgresFkViolation(error) {
  const code = String(error?.code || "");
  const msg = String(error?.message || "").toLowerCase();
  return code === "23503" || msg.includes("foreign key") || msg.includes("violates foreign key");
}

function isMissingColumnError(error, colName) {
  const m = (error?.message || "").toLowerCase();
  const c = (colName || "").toLowerCase();
  if (!c) return false;
  return m.includes(c) && (m.includes("does not exist") || m.includes("column") || m.includes("schema") || m.includes("unknown"));
}

function mapNegotiationRow(row) {
  if (!row) return row;
  const offers = (row.negotiation_offers || row.offers || [])
    .slice()
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
    .map((o) => ({
      ...o,
      from_user_id: o.from_user_id ?? o.author_id,
    }));
  return {
    ...row,
    negotiation_id: row.negotiation_id ?? row.id,
    source_dossier_id: row.source_dossier_id ?? null,
    offers,
  };
}

/** Liste des dossiers : `*` pour compatibilité schémas ; enrichir avec une vue SQL si besoin du vendeur joint. */
const DOSSIER_SELECT = "*";
const CATALOGUE_LIST_LIMIT = 600;

function parseCustomFieldFiltersParam(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Filtre sur champs de qualification (JSON) — appliqué après la requête, sur un périmètre restreint (produit, secteur…).
 */
function dossierMatchCustomFieldFilters(dossier, customFilters) {
  if (!customFilters || typeof customFilters !== "object") return true;
  const cf =
    dossier && typeof dossier.custom_fields === "object" && dossier.custom_fields && !Array.isArray(dossier.custom_fields)
      ? dossier.custom_fields
      : {};
  for (const k of Object.keys(customFilters)) {
    if (k === "__proto__" || k === "constructor") continue;
    const want = customFilters[k];
    if (want == null || want === "") continue;
    const got = cf[k];
    if (got == null && got !== false && got !== 0) return false;
    if (typeof want === "boolean") {
      if (Boolean(got) !== want) return false;
      continue;
    }
    if (String(want) === "true" || String(want) === "false") {
      if (String(want) === "true" && !got) return false;
      if (String(want) === "false" && got) return false;
      continue;
    }
    const wStr = String(want).trim();
    if (wStr === "") continue;
    const a = String(got).trim().toLowerCase();
    const b = wStr.toLowerCase();
    if (a === b) continue;
    if (!isNaN(Number(want)) && !isNaN(Number(got)) && Number(got) === Number(want)) continue;
    if (a.includes(b) && b.length > 0) continue;
    return false;
  }
  return true;
}

async function getDossiersList(params = {}) {
  const customFilters = parseCustomFieldFiltersParam(
    params.custom_fields_filters ?? params.custom_field_filters ?? null
  );
  let q = supabase.from("dossiers").select(DOSSIER_SELECT);
  if (params.secteur) q = q.eq("secteur", params.secteur);
  if (params.produit_id) q = q.eq("produit_id", params.produit_id);
  if (params.location) q = q.ilike("location", `%${params.location}%`);
  if (params.min_price !== undefined && params.min_price !== "")
    q = q.gte("price", Number(params.min_price));
  if (params.max_price !== undefined && params.max_price !== "")
    q = q.lte("price", Number(params.max_price));
  const searchQ = params.q ? String(params.q).trim().slice(0, 200) : "";
  const ac = new AbortController();
  const tid = setTimeout(() => ac.abort(), 28_000);
  let data;
  let error;
  try {
    q = q.or("status.eq.online,status.is.null").order("created_at", { ascending: false }).limit(CATALOGUE_LIST_LIMIT);
    ({ data, error } = await q.abortSignal(ac.signal));
  } catch (e) {
    if (e?.name === "AbortError" || ac.signal.aborted) {
      throw createHttpError(408, "La marketplace met trop longtemps à répondre. Vérifiez la connexion et la config Supabase.");
    }
    throw e;
  } finally {
    clearTimeout(tid);
  }
  if (error) throw createHttpError(400, error.message);
  // Marketplace : en ligne, pas « hors SaaS » (exclues du site public, lien direct possible)
  const now = Date.now();
  let rows = (data || []).filter((d) => {
    if (d?.hors_saas) return false;
    const st = d?.status == null ? "online" : String(d.status);
    if (st === "online") return true;
    return false;
  });
  if (params.type) {
    const wantedType = normalizeDossierType(params.type);
    rows = rows.filter((d) => normalizeDossierType(d?.type) === wantedType);
  }
  if (customFilters) {
    rows = rows.filter((d) => dossierMatchCustomFieldFilters(d, customFilters));
  }
  if (searchQ) {
    rows = rows.filter((d) => dossierMatchesMarketplaceSearch(d, searchQ));
  }
  return { data: rows.map(mapDossierRow) };
}

async function getDossierOne(id) {
  const { data: authData } = await runAuthLocked(() => supabase.auth.getUser());
  const uid = authData?.user?.id;
  if (uid) {
    const { data: me } = await supabase.from("profiles").select("role").eq("id", uid).maybeSingle();
    if (me?.role === "admin") {
      try {
        const r = await nextFetch("GET", "/admin/dossier", null, { params: { id } });
        const payload = r.data;
        const row = payload && typeof payload === "object" && "data" in payload ? payload.data : payload;
        if (!row) throw createHttpError(404, "Dossier introuvable");
        return { data: mapDossierRow(row) };
      } catch (e) {
        const st = e?.response?.status;
        if (st === 404) throw createHttpError(404, "Dossier introuvable");
        throw e;
      }
    }
  }
  const { data, error } = await supabase
    .from("dossiers")
    .select(DOSSIER_SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw createHttpError(400, error.message);
  if (!data) throw createHttpError(404, "Dossier introuvable");
  return { data: mapDossierRow(data) };
}

async function postDossier(body) {
  const user = await requireUser();
  const { hors_saas, annonce_numero: _ignoreNum, status: stIn, ...rest0 } = body || {};
  const rest = normalizePhoneFieldsInDossierPayload({ ...rest0 });
  // Compat schéma: certaines bases utilisent `titre` (NOT NULL) au lieu de `title`.
  if (rest.titre == null || String(rest.titre).trim() === "") {
    if (rest.title != null && String(rest.title).trim() !== "") {
      rest.titre = rest.title;
    }
  }
  // Normalise la fourchette de budget pour les annonces "recherche".
  if (rest.type === "recherche") {
    const mn = rest.price_min != null && rest.price_min !== "" ? Number(rest.price_min) : null;
    const mx = rest.price_max != null && rest.price_max !== "" ? Number(rest.price_max) : null;
    if (mn != null && Number.isFinite(mn)) rest.price_min = mn;
    if (mx != null && Number.isFinite(mx)) rest.price_max = mx;
    if (rest.price == null || rest.price === "" || !Number.isFinite(Number(rest.price))) {
      // Compat listing : price est utilisé pour trier/filtrer ; on met le max si présent.
      if (mx != null && Number.isFinite(mx)) rest.price = mx;
    }
  } else {
    // Nettoyage si base n'a pas la fonctionnalité
    rest.price_min = rest.price_min ?? null;
    rest.price_max = rest.price_max ?? null;
  }
  if (rest.type && rest.type !== "recherche") {
    delete rest.recherche_criteres_requis;
  }
  if (texteContientCoordonnees(rest?.title) || texteContientCoordonnees(rest?.description)) {
    throw createHttpError(400, messageCoordonneesInterdites);
  }
  let annonceRef = null;
  try {
    const { data: n, error: ne } = await supabase.rpc("next_dossier_annonce_numero");
    if (!ne && n) annonceRef = n;
  } catch {
    /* client sans migration RPC : on retombera en secours */
  }
  if (!annonceRef) {
    const y = new Date().getFullYear();
    const rnd = String(Math.random()).replace(/\D/g, "").slice(-5).padStart(5, "0");
    annonceRef = `RM-${y}-${rnd}${Date.now().toString(36).toUpperCase().slice(-4)}`;
  }
  const { data, error } = await supabase
    .from("dossiers")
    .insert({
      ...rest,
      seller_id: user.id,
      status: stIn || "online",
      hors_saas: Boolean(hors_saas),
      annonce_numero: annonceRef,
    })
    .select()
    .single();
  if (error) {
    const msg = (error.message || "").toLowerCase();
    const newColsUnknown =
      (msg.includes("hors_saas") || msg.includes("annonce_numero") || isMissingDossierColumnError(error, "status")) &&
      (msg.includes("column") || msg.includes("does not exist") || msg.includes("schema") || msg.includes("unknown"));
    const requisColMissing = isMissingDossierColumnError(error, "recherche_criteres_requis");
    if (newColsUnknown || requisColMissing) {
      const row = { ...rest, seller_id: user.id };
      if (requisColMissing) {
        delete row.recherche_criteres_requis;
      }
      if (!isMissingDossierColumnError(error, "status")) {
        row.status = stIn || "online";
      }
      const { data: d2, error: e2 } = await supabase.from("dossiers").insert(row).select().single();
      if (e2) throw createHttpError(400, e2.message);
      return { data: mapDossierRow(d2) };
    }
    throw createHttpError(400, error.message);
  }
  return { data: mapDossierRow(data) };
}

async function fetchProduitChampsByProduitIds(produitIds) {
  const ids = [...new Set((produitIds || []).map((x) => String(x)).filter(Boolean))];
  const byId = {};
  if (!ids.length) return byId;
  const { data, error } = await supabase.from("produit_champs").select("*").in("produit_id", ids);
  if (error) throw createHttpError(400, error.message);
  for (const row of data || []) {
    const pid = row.produit_id;
    if (!pid) continue;
    if (!byId[pid]) byId[pid] = [];
    byId[pid].push(row);
  }
  return byId;
}

async function getProduits() {
  const { data: rows, error } = await supabase.from("produits").select("*");
  if (error) throw createHttpError(400, error.message);
  const list = rows || [];
  const champsById = await fetchProduitChampsByProduitIds(list.map((r) => r.id));
  return {
    data: list.map((row) =>
      mapProduitRow({
        ...row,
        produit_champs: champsById[row.id] || row.produit_champs || [],
      }),
    ),
  };
}

async function getProduitOne(produitId) {
  const id = String(produitId || "").trim();
  if (!id) throw createHttpError(400, "produit_id requis");
  const { data: row, error } = await supabase.from("produits").select("*").eq("id", id).maybeSingle();
  if (error) throw createHttpError(400, error.message);
  if (!row) throw createHttpError(404, "Produit introuvable");
  const champsById = await fetchProduitChampsByProduitIds([id]);
  return mapProduitRow({ ...row, produit_champs: champsById[id] || [] });
}

/** Champs dynamiques normalisés avant delete+insert (évite perte si libellé vide ou clés parasites). */
function prepareChampsForSave(champs) {
  if (!Array.isArray(champs)) return [];
  return champs
    .map((c) => {
      const nom = produitChampLabelText(c);
      let type = c?.type ?? c?.champ_type ?? "text";
      if (type === "select" || type === "dropdown") type = "list";
      const known = new Set(["text", "number", "list", "boolean"]);
      if (!known.has(String(type))) type = "text";
      const options = type === "list" ? coalesceProduitChampOptions(c?.options) : [];
      const cible = c?.cible_annonces;
      return {
        nom,
        type,
        options,
        cible_annonces: cible === "vente" || cible === "recherche" ? cible : "tous",
        required: Boolean(c?.required),
        visible_public: c?.visible_public !== false,
      };
    })
    .filter((c) => c.nom.length > 0);
}

async function annuaireVerify(country, registrationNumber) {
  // Proxifie via notre API (évite les erreurs DNS/CORS côté navigateur).
  return nextFetch("POST", "/api/kyc/verify", {
    country,
    registration_number: registrationNumber,
  });
}

async function kycSubmit(payload) {
  const user = await requireUser();
  const { error } = await supabase.from("profiles").update(payload).eq("id", user.id);
  if (error) throw createHttpError(400, error.message);
  return { data: { ok: true } };
}

async function getTransactionsMine(params) {
  const user = await requireUser();
  const role = params?.as_role || "buyer";
  let q = supabase.from("transactions").select("*");
  if (role === "buyer") q = q.eq("buyer_id", user.id);
  else q = q.eq("seller_id", user.id);
  const { data, error } = await q.order("created_at", { ascending: false });
  if (error) throw createHttpError(400, error.message);
  const rows = data || [];
  const dossierIds = [...new Set(rows.map((r) => r.dossier_id).filter(Boolean))];
  let dossierById = {};
  if (dossierIds.length) {
    const { data: drows, error: derr } = await supabase
      .from("dossiers")
      .select("id, titre, location, status, type")
      .in("id", dossierIds);
    if (!derr && drows) {
      dossierById = Object.fromEntries(drows.map((d) => [d.id, d]));
    }
  }
  return {
    data: rows.map((row) => {
      const d = dossierById[row.dossier_id];
      const titreRaw = d?.titre ?? "";
      const titre =
        String(titreRaw || "").trim() ||
        (row.dossier_id ? `Annonce ${String(row.dossier_id).slice(0, 8)}…` : "Annonce");
      const comm = Number(row.commission ?? 0);
      const net =
        row.seller_net != null && Number.isFinite(Number(row.seller_net))
          ? Number(row.seller_net)
          : Math.max(0, Number(row.price || 0) - (Number.isFinite(comm) ? comm : 0));
      return {
        ...row,
        transaction_id: row.transaction_id ?? row.id,
        dossier_title: titre,
        dossier_location: d?.location ?? row.dossier_location ?? null,
        dossier_status: d?.status ?? row.dossier_status ?? null,
        seller_net: net,
      };
    }),
  };
}

async function getDossiersMine() {
  const user = await requireUser();
  const { data, error } = await supabase
    .from("dossiers")
    .select(DOSSIER_SELECT)
    .eq("seller_id", user.id)
    .neq("status", "retire")
    .order("created_at", { ascending: false });
  if (error) throw createHttpError(400, error.message);
  return { data: (data || []).map(mapDossierRow) };
}

function normalizeDossierUpdatePayload(body) {
  const payload = body && typeof body === "object" ? { ...body } : {};
  if (payload.title != null && (payload.titre == null || String(payload.titre).trim() === "")) {
    payload.titre = payload.title;
  }
  if (payload.type === "recherche") {
    if (payload.price_min != null && payload.price_min !== "") payload.price_min = Number(payload.price_min);
    if (payload.price_max != null && payload.price_max !== "") payload.price_max = Number(payload.price_max);
    if (payload.price == null || payload.price === "") {
      payload.price = payload.price_max != null ? Number(payload.price_max) : null;
    }
    return payload;
  }
  return normalizePhoneFieldsInDossierPayload(payload);
}

async function updateDossierMine(dossierId, body) {
  const user = await requireUser();
  const payload = normalizeDossierUpdatePayload(body);
  const { data, error } = await supabase
    .from("dossiers")
    .update(payload)
    .eq("id", dossierId)
    .eq("seller_id", user.id)
    .select(DOSSIER_SELECT)
    .single();
  if (error) throw createHttpError(400, error.message);
  return { data: mapDossierRow(data) };
}

async function putAdminUpdateDossier(dossierId, body) {
  await requireAdmin();
  const id = String(dossierId || "").trim();
  if (!id) throw createHttpError(400, "dossier_id requis");
  const payload = normalizeDossierUpdatePayload(body);
  const { data, error } = await supabase.from("dossiers").update(payload).eq("id", id).select(DOSSIER_SELECT).single();
  if (error) throw createHttpError(400, error.message || "Modification impossible");
  if (!data) throw createHttpError(404, "Annonce introuvable");
  const [mapped] = await adminDossierRowsWithSellerProfiles([data]);
  return { data: mapped };
}

async function retireDossierMine(dossierId, userId) {
  const { data, error } = await supabase
    .from("dossiers")
    .update({ status: "retire" })
    .eq("id", dossierId)
    .eq("seller_id", userId)
    .select("id");
  if (error) {
    if (isMissingDossierColumnError(error, "status")) {
      throw createHttpError(
        400,
        "Impossible de retirer l’annonce : colonne `dossiers.status` manquante (migration 003).",
      );
    }
    throw createHttpError(400, error.message || "Retrait impossible.");
  }
  if (!data?.length) {
    throw createHttpError(404, "Annonce introuvable ou retrait refusé.");
  }
}

/** True si l’annonce ne peut pas être supprimée physiquement (vente / négociation liée). */
async function dossierHasDeletionBlockers(dossierId) {
  const [txRes, negoRes] = await Promise.all([
    supabase.from("transactions").select("id").eq("dossier_id", dossierId).limit(1),
    supabase.from("negotiations").select("id").eq("dossier_id", dossierId).limit(1),
  ]);
  if (!txRes.error && (txRes.data || []).length > 0) return true;
  if (!negoRes.error && (negoRes.data || []).length > 0) return true;
  return false;
}

async function deleteDossierMine(dossierId) {
  const user = await requireUser();
  const { data: row, error: readErr } = await supabase
    .from("dossiers")
    .select("id, seller_deletion_locked_until")
    .eq("id", dossierId)
    .eq("seller_id", user.id)
    .maybeSingle();
  if (readErr) throw createHttpError(400, readErr.message);
  if (!row?.id) throw createHttpError(404, "Annonce introuvable.");
  const lockUntil = row.seller_deletion_locked_until ? new Date(row.seller_deletion_locked_until) : null;
  if (lockUntil && !Number.isNaN(lockUntil.getTime()) && lockUntil.getTime() > Date.now()) {
    throw createHttpError(
      403,
      "Suppression impossible pendant la période de blocage (72 h après clôture du rendez-vous). L’annonce disparaîtra automatiquement ensuite. En cas de litige résolu en votre faveur après un refus, l’annonce peut être remise en ligne immédiatement.",
    );
  }

  if (await dossierHasDeletionBlockers(dossierId)) {
    await retireDossierMine(dossierId, user.id);
    return {
      data: {
        ok: true,
        removed: "retired",
        detail:
          "Cette annonce a déjà une vente ou une négociation : elle a été retirée de « Mes annonces ». L’historique (transactions, appels) est conservé.",
      },
    };
  }

  const { error } = await supabase.from("dossiers").delete().eq("id", dossierId).eq("seller_id", user.id);
  if (error) {
    if (isPostgresFkViolation(error)) {
      await retireDossierMine(dossierId, user.id);
      return {
        data: {
          ok: true,
          removed: "retired",
          detail:
            "Suppression impossible car l’annonce est liée à d’autres données : elle a été retirée de « Mes annonces » à la place.",
        },
      };
    }
    throw createHttpError(400, error.message || "Suppression impossible.");
  }
  return { data: { ok: true, removed: "deleted" } };
}

async function getNegotiationsReceived() {
  const user = await requireUser();
  const { data, error } = await supabase
    .from("negotiations")
    .select("*, negotiation_offers(*), dossiers(*)")
    .eq("seller_id", user.id)
    .order("created_at", { ascending: false });
  if (error) throw createHttpError(400, error.message);
  return { data: (data || []).map(mapNegotiationRow) };
}

async function getNegotiationsMine() {
  const user = await requireUser();
  const { data, error } = await supabase
    .from("negotiations")
    .select("*, negotiation_offers(*), dossiers(*)")
    .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
    .order("created_at", { ascending: false });
  if (error) throw createHttpError(400, error.message);
  const mapped = (data || []).map(mapNegotiationRow);
  return { data: mapped.filter((n) => (n.offers?.length || 0) > 0) };
}

async function deleteNegotiationMine(negotiationId) {
  const user = await requireUser();
  // Supprime uniquement si la négociation appartient à l’utilisateur (buyer ou seller).
  let r = await supabase
    .from("negotiations")
    .delete()
    .eq("id", negotiationId)
    .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);
  if (r.error) {
    r = await supabase
      .from("negotiations")
      .delete()
      .eq("negotiation_id", negotiationId)
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);
  }
  if (r.error) throw createHttpError(400, r.error.message);
  return { data: { ok: true } };
}

async function getDisputesMine() {
  const user = await requireUser();
  const { data: txs, error: te } = await supabase
    .from("transactions")
    .select("id, buyer_id, seller_id, dossier_id, price, buyer_total, statut")
    .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`);
  if (te) throw createHttpError(400, te.message);
  const txList = txs || [];
  const ids = txList.map((t) => t.id).filter(Boolean);
  if (!ids.length) return { data: [] };

  const txById = Object.fromEntries(txList.map((t) => [t.id, t]));
  const dossierIds = [...new Set(txList.map((t) => t.dossier_id).filter(Boolean))];
  let dossierById = {};
  if (dossierIds.length) {
    const { data: drows, error: de } = await supabase
      .from("dossiers")
      .select("id, titre, title, type, status")
      .in("id", dossierIds);
    if (de) throw createHttpError(400, de.message);
    dossierById = Object.fromEntries((drows || []).map((d) => [d.id, d]));
  }

  const { data, error } = await supabase
    .from("litiges")
    .select("*")
    .in("transaction_id", ids)
    .order("created_at", { ascending: false });
  if (error) throw createHttpError(400, error.message);

  const rows = (data || []).map((l) => {
    const tid = l.transaction_id;
    const tx = tid ? txById[tid] : null;
    const dossierId = tx?.dossier_id ?? l.dossier_id ?? null;
    const dossier = dossierId ? dossierById[dossierId] : null;
    const titreRaw = dossier?.titre ?? dossier?.title ?? "";
    const dossier_title =
      String(titreRaw || "").trim() ||
      (dossierId ? `Annonce ${String(dossierId).slice(0, 8)}…` : null);
    return {
      ...l,
      dispute_id: l.dispute_id ?? l.id,
      status: l.status ?? l.statut ?? null,
      buyer_id: tx?.buyer_id ?? l.buyer_id ?? null,
      seller_id: tx?.seller_id ?? l.seller_id ?? null,
      dossier_id: dossierId,
      dossier_title,
      dossier_type: dossier?.type ?? null,
      transaction_statut: tx?.statut ?? null,
      transaction_amount:
        tx?.buyer_total != null && tx.buyer_total !== ""
          ? Number(tx.buyer_total)
          : tx?.price != null
            ? Number(tx.price)
            : null,
    };
  });
  return { data: rows };
}

async function getDossierNegotiationMine(dossierId) {
  const user = await requireUser();
  const { data, error } = await supabase
    .from("negotiations")
    .select("*, negotiation_offers(*)")
    .eq("dossier_id", dossierId)
    .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw createHttpError(400, error.message);
  return { data: data ? mapNegotiationRow(data) : null };
}

async function getDossierMessages(dossierId) {
  await requireUser();
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("created_at", { ascending: true });
  if (error) throw createHttpError(400, error.message);
  return { data: data || [] };
}

async function insertNegotiationTolerant(ins) {
  const r1 = await supabase.from("negotiations").insert(ins).select().single();
  if (!r1.error) {
    return r1;
  }
  const msg = String(r1.error?.message || "");
  if (ins.source_dossier_id && (msg.includes("source_dossier_id") || (msg.includes("column") && msg.includes("schema")))) {
    const { source_dossier_id, ...rest } = ins;
    return supabase.from("negotiations").insert(rest).select().single();
  }
  return r1;
}

async function postDossierNegotiation(dossierId, body = null) {
  const user = await requireUser();
  const payload = body && typeof body === "object" ? body : {};
  const { data: dossier, error: de } = await supabase
    .from("dossiers")
    .select("seller_id, type, produit_id, status, custom_fields, recherche_criteres_requis, price_min, price_max")
    .eq("id", dossierId)
    .single();
  if (de || !dossier) throw createHttpError(404, "Dossier introuvable");
  if (dossier.seller_id === user.id) {
    throw createHttpError(400, "Vous ne pouvez pas négocier sur votre propre annonce");
  }
  if (dossier.status && dossier.status !== "online") {
    throw createHttpError(400, "Cette annonce n’est plus disponible pour de nouveaux échanges");
  }
  if (dossier.type === "recherche") {
    const sourceDossierId = payload?.source_dossier_id;
    if (!sourceDossierId) {
      throw createHttpError(
        400,
        "Pour une annonce « Je cherche », indiquez votre fiche de vente du même type (champs d’adéquation) : source_dossier_id requis"
      );
    }
    const { data: source, error: se } = await supabase
      .from("dossiers")
      .select("seller_id, type, produit_id, status, custom_fields, id")
      .eq("id", sourceDossierId)
      .single();
    if (se || !source) throw createHttpError(400, "Fiche de vente liée introuvable");
    if (String(source.seller_id) !== String(user.id)) {
      throw createHttpError(403, "La fiche de vente liée doit être l’une de vos annonces");
    }
    if (source.type === "recherche") {
      throw createHttpError(400, "Indiquez une annonce de vente, pas une autre demande d’achat");
    }
    if (String(source.produit_id) !== String(dossier.produit_id)) {
      throw createHttpError(400, "Le produit de votre fiche de vente doit être le même que sur la demande d’achat");
    }
    if (source.status !== "online") {
      throw createHttpError(400, "L’annonce de vente liée doit être en ligne (marketplace)");
    }
    if (!offreVenteCorrespondARecherche(dossier, source)) {
      throw createHttpError(
        400,
        "Votre fiche de vente ne respecte pas les critères d’adéquation exigés sur cette annonce d’achat (champs marqués obligatoires par l’annonceur). Mettez à jour votre fiche de vente pour qu’elle coïncide, ou choisissez une autre demande."
      );
    }
  }
  const insert = {
    dossier_id: dossierId,
    buyer_id: user.id,
    seller_id: dossier.seller_id,
    status: "open",
  };
  if (dossier.type === "recherche" && payload.source_dossier_id) {
    insert.source_dossier_id = payload.source_dossier_id;
  }
  const { data, error } = await insertNegotiationTolerant(insert);
  if (error) throw createHttpError(400, error.message);
  return { data: mapNegotiationRow({ ...data, negotiation_offers: [] }) };
}

function assertOfferWithinBudget(dossier, amount) {
  if (!dossier || dossier.type !== "recherche") return;
  const mn = dossier.price_min != null && dossier.price_min !== "" ? Number(dossier.price_min) : null;
  const mx = dossier.price_max != null && dossier.price_max !== "" ? Number(dossier.price_max) : null;
  if (!Number.isFinite(amount)) return;
  if (mn != null && Number.isFinite(mn) && amount < mn) {
    throw createHttpError(400, `Votre proposition (${amount}€) est en dessous du budget minimum (${mn}€).`);
  }
  if (mx != null && Number.isFinite(mx) && amount > mx) {
    throw createHttpError(400, `Votre proposition (${amount}€) dépasse le budget maximum (${mx}€).`);
  }
}

async function postNegotiationOffer(negotiationId, body) {
  const user = await requireUser();
  const amt = Number(body?.amount);
  if (!amt || amt <= 0) throw createHttpError(400, "Montant de l'offre invalide");
  // Contrôle budget sur les annonces "recherche"
  const { data: neg, error: ne } = await supabase
    .from("negotiations")
    .select("id, dossier_id")
    .eq("id", negotiationId)
    .single();
  if (ne || !neg) throw createHttpError(404, "Négociation introuvable");
  const { data: d, error: de } = await supabase
    .from("dossiers")
    .select("type, price_min, price_max")
    .eq("id", neg.dossier_id)
    .maybeSingle();
  if (de) throw createHttpError(400, de.message);
  assertOfferWithinBudget(d, amt);
  const { error } = await supabase.from("negotiation_offers").insert({
    negotiation_id: negotiationId,
    from_user_id: user.id,
    amount: amt,
    message: body.message || "",
  });
  if (error) throw createHttpError(400, error.message);
  return { data: { ok: true } };
}

async function postNegotiationRespond(negotiationId, params) {
  const user = await requireUser();
  const action = params.action;
  const { data: neg, error } = await supabase
    .from("negotiations")
    .select("*, negotiation_offers(*)")
    .eq("id", negotiationId)
    .single();
  if (error || !neg) throw createHttpError(404, "Négociation introuvable");
  const offers = (neg.negotiation_offers || []).sort(
    (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
  );
  const last = offers[offers.length - 1];
  if (action === "accept") {
    const { error: uerr } = await supabase
      .from("negotiations")
      .update({ status: "accepted", final_price: last?.amount ?? null })
      .eq("id", negotiationId);
    if (uerr) throw createHttpError(400, uerr.message);

    // Une annonce de vente acceptée ne doit plus apparaître sur la marketplace :
    // on la passe en "reserved" (sans expiration) jusqu'à l'issue (achat ou refus/annulation).
    try {
      const dossierId = neg?.dossier_id;
      if (dossierId) {
        const { data: d } = await supabase
          .from("dossiers")
          .select("id, type, status")
          .eq("id", dossierId)
          .maybeSingle();
        const t = String(d?.type || "").toLowerCase().trim();
        if (t !== "recherche" && String(d?.status || "online") === "online") {
          await supabase
            .from("dossiers")
            .update({ status: "reserved", reserved_until: null, reserved_transaction_id: null })
            .eq("id", dossierId);
        }
      }
    } catch {
      // ne bloque pas l'acceptation si la réserve échoue (RLS / colonnes manquantes).
    }
  } else if (action === "reject") {
    const { error: uerr } = await supabase.from("negotiations").update({ status: "rejected" }).eq("id", negotiationId);
    if (uerr) throw createHttpError(400, uerr.message);
  } else if (action === "counter") {
    const amt = Number(params.counter_amount);
    if (!amt || amt <= 0) throw createHttpError(400, "Montant de contre-offre invalide");
    // Contrôle budget sur les annonces "recherche"
    const { data: d, error: de } = await supabase
      .from("dossiers")
      .select("type, price_min, price_max")
      .eq("id", neg.dossier_id)
      .maybeSingle();
    if (de) throw createHttpError(400, de.message);
    assertOfferWithinBudget(d, amt);
    const { error: ierr } = await supabase.from("negotiation_offers").insert({
      negotiation_id: negotiationId,
      from_user_id: user.id,
      amount: amt,
      message: "Contre-offre",
    });
    if (ierr) throw createHttpError(400, ierr.message);
  } else throw createHttpError(400, "Action inconnue");
  return { data: { ok: true } };
}

async function postDossierMessage(dossierId, body) {
  const user = await requireUser();
  const { error } = await supabase.from("messages").insert({
    dossier_id: dossierId,
    author_id: user.id,
    content: body.content,
  });
  if (error) throw createHttpError(400, error.message);
  return { data: { ok: true } };
}

async function postDossierBuy(dossierId, params) {
  const token = await getAccessToken();
  const base = nextApiBase();
  if (!base) throw createHttpError(500, "REACT_APP_NEXT_API_URL requis pour le paiement");
  const res = await fetch(`${base}/api/acheter`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({
      dossier_id: dossierId,
      negotiation_id: params.negotiation_id ?? null,
      ...(params.payment_method_id
        ? { payment_method_id: String(params.payment_method_id) }
        : {}),
      return_url:
        typeof window !== "undefined"
          ? `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash || ""}`
          : null,
    }),
  });
  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { detail: text };
  }
  if (!res.ok) throw createHttpError(res.status, json.detail || json.message || "Erreur paiement", json);
  return { data: json };
}

async function getMySubscription(config = {}) {
  // Utilise l’API serveur (service role) pour éviter les blocages RLS côté client.
  return nextFetch("GET", "/api/subscriptions/me", null, config || {});
}

async function postConnectStart(body) {
  return nextFetch("POST", "/api/connect/start", body || {}, {});
}

async function getConnectStatus() {
  return nextFetch("GET", "/api/connect/status", null, {});
}

async function getConnectBalance() {
  return nextFetch("GET", "/api/connect/balance", null, {});
}

async function postConnectDashboard() {
  return nextFetch("POST", "/api/connect/dashboard", {}, {});
}

function normTransactionId(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

async function postRatings(body) {
  const user = await requireUser();
  const txIdRaw = body?.transaction_id || body?.transactionId;
  const txId = normTransactionId(txIdRaw);
  if (!txId) throw createHttpError(400, "transaction_id requis");

  const { data: existing } = await supabase
    .from("notations")
    .select("id")
    .eq("transaction_id", txId)
    .eq("author_id", user.id)
    .maybeSingle();
  if (existing?.id) {
    return { data: { ok: true, alreadyRated: true } };
  }

  const { data: tx } = await supabase
    .from("transactions")
    .select("buyer_id,seller_id")
    .eq("id", txId)
    .maybeSingle();
  const rated_user_id =
    tx && tx.buyer_id === user.id ? tx.seller_id : tx?.buyer_id ?? null;
  const row = {
    transaction_id: txId,
    stars: body.stars,
    comment: body.comment || "",
    author_id: user.id,
    ...(rated_user_id ? { rated_user_id } : {}),
  };
  const { error } = await supabase.from("notations").insert(row);
  if (error) {
    const msg = String(error.message || "").toLowerCase();
    if (msg.includes("row-level security") || msg.includes("rls")) {
      throw createHttpError(
        503,
        "Mise à jour base requise : exécutez db/migrations/055_notations_insert_rls_definer.sql dans Supabase (SQL) — ou réappliquez la 054 à jour puis rechargez le schéma API.",
      );
    }
    const missingNotationCol = ["author_id", "comment", "stars", "transaction_id", "rated_user_id"].find((col) =>
      isMissingColumnError(error, col),
    );
    if (missingNotationCol) {
      throw createHttpError(
        503,
        "Mise à jour base requise : exécutez dans Supabase (SQL) les fichiers db/migrations/052_notations_author_id.sql puis 053_notations_rating_columns.sql, puis réessayez.",
      );
    }
    const dup = String(error.message || "").toLowerCase();
    if (dup.includes("unique") || dup.includes("duplicate") || dup.includes("idx_notations_transaction_author")) {
      return { data: { ok: true, alreadyRated: true } };
    }
    throw createHttpError(400, error.message);
  }
  return { data: { ok: true } };
}

/** Aligné sur api/disputes.js : fenêtre 72 h après clôture (capture / mise à jour). */
const DISPUTE_OPEN_HOURS_CLIENT = SELLER_PAYOUT_HOURS;

function isDisputeOpenWindowClosedClient(txRow) {
  if (DISPUTE_OPEN_HOURS_CLIENT <= 0) return false;
  const st = String(txRow?.statut || "").toLowerCase();
  if (st === "litige") return true;
  if (st !== "fonds_debloques" && st !== "rembourse") return true;
  const raw = txRow?.capture_confirmed_at ?? txRow?.updated_at;
  if (!raw) return false;
  const anchor = Date.parse(String(raw));
  if (!Number.isFinite(anchor)) return false;
  return Date.now() > anchor + DISPUTE_OPEN_HOURS_CLIENT * 3600 * 1000;
}

async function postDisputes(body) {
  const user = await requireUser();
  const transactionId = body?.transaction_id || body?.transactionId;
  if (!transactionId) throw createHttpError(400, "transaction_id requis");

  const { data: tx, error: txErr } = await supabase
    .from("transactions")
    .select("id, buyer_id, seller_id, statut, capture_confirmed_at, updated_at, ai_analysis")
    .eq("id", transactionId)
    .maybeSingle();
  if (txErr) throw createHttpError(400, txErr.message);
  if (!tx) throw createHttpError(404, "Transaction introuvable");
  if (tx.buyer_id !== user.id && tx.seller_id !== user.id) {
    throw createHttpError(403, "Non autorisé à ouvrir un litige sur cette transaction");
  }
  const st0 = String(tx.statut || "").toLowerCase();
  if (st0 !== "fonds_debloques" && st0 !== "rembourse") {
    throw createHttpError(
      400,
      st0 === "litige"
        ? "Un litige est déjà en cours sur cette transaction."
        : "Vous ne pouvez ouvrir un litige qu’après avoir terminé l’étape « Confirmer / refuser le RDV » (décision enregistrée).",
    );
  }
  if (isDisputeOpenWindowClosedClient(tx)) {
    throw createHttpError(
      400,
      `Le délai de ${DISPUTE_OPEN_HOURS_CLIENT || 72} heures pour ouvrir un litige après la clôture de ce rendez-vous est dépassé.`,
    );
  }

  if (isDisputeOpeningBlockedByPlatform(tx)) {
    throw createHttpError(400, OPEN_DISPUTE_BLOCKED_PLATFORM_DETAIL_FR);
  }

  const { data: lits, error: litErr } = await supabase.from("litiges").select("*").eq("transaction_id", transactionId);
  if (litErr) throw createHttpError(400, litErr.message);
  const list = lits || [];
  if (list.length > 0) {
    const hasOpen = list.some((l) => !isLitigeRowClosed(l));
    throw createHttpError(
      400,
      hasOpen
        ? "Un litige est déjà ouvert pour cette transaction."
        : "Un litige a déjà été traité sur cette transaction. Il n’est pas possible d’en ouvrir un second.",
    );
  }

  const row = {
    transaction_id: transactionId,
    reason: body.reason,
    description: body.description,
    opened_by: user.id,
  };
  const { error } = await supabase.from("litiges").insert(row);
  if (error) {
    const m = (error.message || "").toLowerCase();
    if (m.includes("opened_by") || (m.includes("column") && m.includes("does not exist"))) {
      const { error: e2 } = await supabase
        .from("litiges")
        .insert({
          transaction_id: body.transaction_id,
          reason: body.reason,
          description: body.description,
        });
      if (e2) throw createHttpError(400, e2.message);
    } else {
      throw createHttpError(400, error.message);
    }
  }
  return { data: { ok: true } };
}

async function adminDossierRowsWithSellerProfiles(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const sellerIds = [...new Set(list.map((r) => r.seller_id).filter(Boolean))];
  const profBy = {};
  if (sellerIds.length) {
    const { data: profs, error } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, name, nom, email, phone, telephone")
      .in("id", sellerIds);
    if (error) console.warn("[RdvMarket] admin dossiers — lecture profils vendeur :", error.message);
    (profs || []).forEach((p) => {
      if (p?.id) profBy[p.id] = p;
    });
  }
  return list.map((r) => mapDossierRow(r, profBy[r.seller_id] || null));
}

async function getAdminDossiersByStatus(status) {
  await requireAdmin();
  const { data, error } = await supabase
    .from("dossiers")
    .select(DOSSIER_SELECT)
    .eq("status", status)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) {
    if (isMissingDossierColumnError(error, "status")) {
      const { data: all, error: e2 } = await supabase
        .from("dossiers")
        .select(DOSSIER_SELECT)
        .order("created_at", { ascending: false })
        .limit(500);
      if (e2) throw createHttpError(400, e2.message);
      const rows = all || [];
      console.warn(
        "[RdvMarket] Colonne `dossiers.status` absente : exécuter db/migrations/003_dossiers_status_moderation.sql sur Supabase (ou liste dégradée côté admin)."
      );
      if (status === "pending" || status === "online") {
        return { data: await adminDossierRowsWithSellerProfiles(rows) };
      }
      return { data: [] };
    }
    throw createHttpError(400, error.message);
  }
  return { data: await adminDossierRowsWithSellerProfiles(data || []) };
}

async function putAdminWithdrawDossier(dossierId) {
  await requireAdmin();
  const id = String(dossierId || "").trim();
  if (!id) throw createHttpError(400, "dossier_id requis");
  try {
    return await nextFetch("PUT", `/admin/dossiers/${encodeURIComponent(id)}/withdraw`, null, {});
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
  }
  let { error } = await supabase.from("dossiers").update({ status: "hidden" }).eq("id", id);
  if (error) {
    ({ error } = await supabase.from("dossiers").update({ status: "archived" }).eq("id", id));
  }
  if (error) {
    if (isMissingDossierColumnError(error, "status")) {
      throw createHttpError(
        400,
        "Colonne `dossiers.status` manquante : exécutez `db/migrations/003_dossiers_status_moderation.sql` sur Supabase, puis réessayez."
      );
    }
    throw createHttpError(400, error.message);
  }
  return { data: { ok: true } };
}

async function putAdminPublishDossier(dossierId) {
  await requireAdmin();
  const id = String(dossierId || "").trim();
  if (!id) throw createHttpError(400, "dossier_id requis");
  try {
    return await nextFetch("PUT", `/admin/dossiers/${encodeURIComponent(id)}/publish`, null, {});
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
  }
  const { error } = await supabase.from("dossiers").update({ status: "online" }).eq("id", id);
  if (error) {
    if (isMissingDossierColumnError(error, "status")) {
      throw createHttpError(400, "Colonne `dossiers.status` manquante (migration 003).");
    }
    throw createHttpError(400, error.message);
  }
  return { data: { ok: true, status: "online" } };
}

async function deleteAdminDossier(dossierId) {
  await requireAdmin();
  const id = String(dossierId || "").trim();
  if (!id) throw createHttpError(400, "dossier_id requis");
  try {
    return await nextFetch("DELETE", `/admin/dossiers/${encodeURIComponent(id)}/delete`, null, {});
  } catch (err) {
    if (err?.response?.status !== 404) throw err;
  }
  if (await dossierHasDeletionBlockers(id)) {
    await supabase.from("dossiers").update({ status: "retire" }).eq("id", id);
    return {
      data: {
        ok: true,
        removed: "retired",
        detail:
          "Cette annonce est liée à des transactions ou négociations : elle a été retirée du catalogue. L’historique est conservé.",
      },
    };
  }
  const { error } = await supabase.from("dossiers").delete().eq("id", id);
  if (error) {
    if (isPostgresFkViolation(error)) {
      await supabase.from("dossiers").update({ status: "retire" }).eq("id", id);
      return {
        data: {
          ok: true,
          removed: "retired",
          detail:
            "Suppression physique impossible (données liées) : l’annonce a été retirée du catalogue à la place.",
        },
      };
    }
    throw createHttpError(400, error.message || "Suppression impossible.");
  }
  return { data: { ok: true, removed: "deleted", detail: "Annonce supprimée définitivement." } };
}

async function getAdminCatalogBroadcast(params = {}) {
  await requireAdmin();
  const secteur = String(params.secteur || "all").trim() || "all";
  const rawIds = params.produit_ids ?? params.produit_id ?? "";
  const produitIds =
    rawIds === "" || rawIds === "all"
      ? []
      : String(rawIds)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

  const { data: dossiers, error: de } = await supabase
    .from("dossiers")
    .select("*")
    .eq("status", "online")
    .order("created_at", { ascending: false })
    .limit(800);
  if (de) throw createHttpError(400, de.message);
  const rows = (dossiers || []).filter((d) => !d?.hors_saas);

  const { data: prods, error: pe } = await supabase.from("produits").select("id, nom, secteur, actif").eq("actif", true);
  if (pe) throw createHttpError(400, pe.message);
  const produitsById = {};
  for (const p of prods || []) {
    const pid = String(p?.id ?? p?.produit_id ?? "").trim();
    if (pid) produitsById[pid] = p;
  }

  const siteUrl = getPublicSiteUrl();

  const digest = buildCatalogBroadcastMessage({
    dossiers: rows,
    produitsById,
    filters: { secteur, produitIds },
    options: { siteUrl },
  });
  return { data: digest };
}

async function getAdminDossiersHidden() {
  await requireAdmin();
  const { data, error } = await supabase
    .from("dossiers")
    .select(DOSSIER_SELECT)
    .in("status", ["hidden", "archived", "withdrawn", "rejected"])
    .order("updated_at", { ascending: false })
    .limit(300);
  if (error) {
    if (isMissingDossierColumnError(error, "status")) return { data: [] };
    throw createHttpError(400, error.message);
  }
  return { data: await adminDossierRowsWithSellerProfiles(data || []) };
}

async function getAdminDisputesEnriched() {
  await requireAdmin();
  const { data: litiges, error: le } = await supabase
    .from("litiges")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(500);
  if (le) throw createHttpError(400, le.message);
  const list = litiges || [];
  const tids = [...new Set(list.map((l) => l.transaction_id).filter(Boolean))];
  const txById = {};
  if (tids.length) {
    const { data: txs, error: te } = await supabase.from("transactions").select("*").in("id", tids);
    if (te) throw createHttpError(400, te.message);
    const dIds = [...new Set((txs || []).map((t) => t.dossier_id).filter(Boolean))];
    const dossierBy = {};
    if (dIds.length) {
      const { data: drows, error: de } = await supabase
        .from("dossiers")
        .select(DOSSIER_SELECT)
        .in("id", dIds);
      if (de) throw createHttpError(400, de.message);
      (drows || []).forEach((d) => {
        dossierBy[d.id] = d;
      });
    }
    (txs || []).forEach((t) => {
      txById[t.id] = t.dossier_id && dossierBy[t.dossier_id] ? { ...t, dossier: mapDossierRow(dossierBy[t.dossier_id]) } : { ...t, dossier: null };
    });
  }
  const sessionsByTid = {};
  if (tids.length) {
    const { data: sess } = await supabase.from("call_sessions").select("*").in("transaction_id", tids);
    (sess || []).forEach((s) => {
      if (!s.transaction_id) return;
      const ex = sessionsByTid[s.transaction_id];
      const st = s.started_at || s.created_at;
      if (!ex || (st && ex._ts < st) || (s.duration_seconds && !ex?.duration_seconds)) {
        sessionsByTid[s.transaction_id] = { ...s, _ts: st };
      }
    });
  }
  const uids = new Set();
  list.forEach((l) => l.opened_by && uids.add(l.opened_by));
  Object.values(txById).forEach((t) => {
    if (t.buyer_id) uids.add(t.buyer_id);
    if (t.seller_id) uids.add(t.seller_id);
  });
  const nameBy = {};
  const profBy = {};
  if (uids.size) {
    const { data: profs } = await supabase
      .from("profiles")
      .select(
        "id, first_name, last_name, name, nom, email, phone, telephone, kyc_status, identity_verified_at, stripe_identity_session_id, rating_avg",
      )
      .in("id", [...uids]);
    (profs || []).forEach((p) => {
      const label = profileDisplayName(p) || String(p.email || "").trim();
      nameBy[p.id] = label || null;
      profBy[p.id] = p;
    });
  }
  /** Moyenne des étoiles reçues (notations.rated_user_id), car profiles.rating_avg n'est pas toujours maintenu. */
  const ratingAvgByRated = {};
  if (uids.size) {
    const uidArr = [...uids];
    const { data: noteRows } = await supabase
      .from("notations")
      .select("rated_user_id, stars")
      .in("rated_user_id", uidArr);
    const agg = {};
    (noteRows || []).forEach((n) => {
      const rid = n.rated_user_id;
      if (!rid) return;
      const s = Number(n.stars);
      if (!Number.isFinite(s)) return;
      if (!agg[rid]) agg[rid] = { sum: 0, count: 0 };
      agg[rid].sum += s;
      agg[rid].count += 1;
    });
    Object.keys(agg).forEach((rid) => {
      const { sum, count } = agg[rid];
      if (count) ratingAvgByRated[rid] = sum / count;
    });
  }
  return {
    data: list.map((l) => {
      const t = l.transaction_id ? txById[l.transaction_id] : null;
      const d = t?.dossier;
      const rec = t ? sessionsByTid[t.id] : null;
      const oId = l.opened_by;
      const acheteur = t?.buyer_id;
      const vendeur = t?.seller_id;
      const vendeurProf = vendeur && profBy[vendeur] ? profBy[vendeur] : null;
      const acheteurProf = acheteur && profBy[acheteur] ? profBy[acheteur] : null;
      let roleDemandeur = "—";
      if (oId && t) {
        if (oId === acheteur) roleDemandeur = "Acheteur";
        else if (oId === vendeur) roleDemandeur = "Vendeur";
        else roleDemandeur = "Partie (autre)";
      }
      return {
        ...l,
        dispute_id: l.dispute_id ?? l.id,
        status: l.status ?? l.statut ?? null,
        transaction: t
          ? {
              ...t,
              dossier: d || null,
            }
          : null,
        dossier: d,
        vendeur_identity: vendeurProf
          ? {
              kyc_status: vendeurProf.kyc_status ?? null,
              identity_verified_at: vendeurProf.identity_verified_at ?? null,
              stripe_identity_session_id: vendeurProf.stripe_identity_session_id ?? null,
            }
          : null,
        prix_depart: d != null && d.price != null ? d.price : null,
        prix_vente_transaction: t != null && (t.amount ?? t.montant ?? t.prix) != null ? t.amount ?? t.montant ?? t.prix : null,
        prix_deal: t != null && (t.final_price != null && t.final_price !== "") ? t.final_price : t != null ? t.amount ?? t.montant : null,
        enregistrement_url: rec?.recording_url,
        duree_appel_s: rec?.duration_seconds,
        synthese_ia_transaction: t?.ai_analysis,
        pre_analyse_ia_litige: l.ai_pre_analysis,
        message_partie: l.description,
        motif: l.reason,
        demandeur_id: oId,
        demandeur_label: oId ? nameBy[oId] : null,
        role_demandeur: roleDemandeur,
        acheteur_id: t?.buyer_id,
        vendeur_id: t?.seller_id,
        acheteur_name: t?.buyer_id ? nameBy[t.buyer_id] : null,
        vendeur_name: t?.seller_id ? nameBy[t.seller_id] : null,
        acheteur_email: acheteurProf?.email ?? null,
        vendeur_email: vendeurProf?.email ?? null,
        acheteur_rating_avg:
          acheteur != null && ratingAvgByRated[acheteur] != null
            ? ratingAvgByRated[acheteur]
            : acheteurProf?.rating_avg ?? null,
        vendeur_rating_avg:
          vendeur != null && ratingAvgByRated[vendeur] != null
            ? ratingAvgByRated[vendeur]
            : vendeurProf?.rating_avg ?? null,
      };
    }),
  };
}

/** Litige : résolution côté serveur (Stripe + remise en vente si remboursement). */
async function postAdminDisputeResolve(disputeId, params) {
  const id = String(disputeId || "").trim();
  if (!id) throw createHttpError(400, "disputeId requis");
  if (!params?.decision) throw createHttpError(400, "decision requise");
  return nextFetch("POST", `/admin/disputes/${encodeURIComponent(id)}/resolve`, params);
}

function isAdminSubscriptionRowActive(row) {
  return isSubscriptionRowPaidActive(row);
}

/** MRR mensualisé (€) à partir du Price Stripe (cents) et du plan. */
function subscriptionRowMrrEur(row) {
  const cents = Number(row?.price_unit_amount_cents ?? NaN);
  if (!Number.isFinite(cents) || cents <= 0) return null;
  const plan = String(row?.plan || "monthly").trim().toLowerCase();
  const monthlyCents = plan === "yearly" ? cents / 12 : cents;
  return monthlyCents / 100;
}

function adminSubscriptionActivatedMs(row) {
  const raw = row?.activated_at;
  if (raw) {
    const t = Date.parse(String(raw));
    if (Number.isFinite(t)) return t;
  }
  if (!isAdminSubscriptionRowActive(row)) return null;
  const end = row?.current_period_end ? Date.parse(String(row.current_period_end)) : NaN;
  const plan = String(row?.plan || "monthly").trim().toLowerCase();
  if (Number.isFinite(end)) {
    const d = new Date(end);
    d.setMonth(d.getMonth() - (plan === "yearly" ? 12 : 1));
    return d.getTime();
  }
  return Date.now() - 30 * 86400000;
}

function adminSubscriptionCanceledMs(row) {
  const raw = row?.canceled_at;
  if (raw) {
    const t = Date.parse(String(raw));
    if (Number.isFinite(t)) return t;
  }
  if (isAdminSubscriptionRowActive(row)) return null;
  const end = row?.current_period_end ? Date.parse(String(row.current_period_end)) : NaN;
  if (Number.isFinite(end) && end < Date.now()) return end;
  return null;
}

/** Série journalière abonnés + churn sur la période (à partir des dates cycle de vie). */
function buildAdminSubscriptionPeriodSeries(rows, dayKeys) {
  const events = Object.fromEntries(dayKeys.map((k) => [k, { new: 0, churned: 0 }]));
  const periodStartMs = Date.parse(`${dayKeys[0]}T00:00:00`);
  const periodEndMs = Date.parse(`${dayKeys[dayKeys.length - 1]}T23:59:59.999`);

  for (const row of rows || []) {
    const actMs = adminSubscriptionActivatedMs(row);
    if (actMs != null) {
      const dk = adminDayKey(actMs);
      if (events[dk]) events[dk].new += 1;
    }
    const cancelMs = adminSubscriptionCanceledMs(row);
    if (cancelMs != null) {
      const dk = adminDayKey(cancelMs);
      if (events[dk]) events[dk].churned += 1;
    }
  }

  const activeNow = (rows || []).filter(isAdminSubscriptionRowActive).length;
  let newInPeriod = 0;
  let churnedInPeriod = 0;
  for (const k of dayKeys) {
    newInPeriod += events[k].new;
    churnedInPeriod += events[k].churned;
  }

  let running = Math.max(0, activeNow - newInPeriod + churnedInPeriod);
  const activeStart = running;
  const series = dayKeys.map((k) => {
    running += events[k].new - events[k].churned;
    running = Math.max(0, running);
    return {
      date: k,
      subscribers_active: running,
      subscribers_new: events[k].new,
      subscribers_churned: events[k].churned,
    };
  });

  const activeEnd = activeNow;
  const churnDenom = activeStart > 0 ? activeStart : activeEnd;
  const churnRatePct =
    churnDenom > 0 ? Math.round((churnedInPeriod / churnDenom) * 1000) / 10 : 0;

  return {
    series,
    subscribers_new: newInPeriod,
    subscribers_churned: churnedInPeriod,
    subscribers_net: newInPeriod - churnedInPeriod,
    subscribers_active_start: activeStart,
    subscribers_active_end: activeEnd,
    churn_rate_pct: churnRatePct,
    has_lifecycle_dates: (rows || []).some((r) => r?.activated_at || r?.canceled_at),
  };
}

/** Abonnés, MRR, ARR + évolution / churn sur la période. */
async function aggregateAdminSubscriptionMetrics(days = 30) {
  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  const dayKeys = adminBuildDayKeys(days);
  const emptyPeriod = {
    days,
    series: dayKeys.map((date) => ({
      date,
      subscribers_active: 0,
      subscribers_new: 0,
      subscribers_churned: 0,
    })),
    subscribers_new: 0,
    subscribers_churned: 0,
    subscribers_net: 0,
    subscribers_active_start: 0,
    subscribers_active_end: 0,
    churn_rate_pct: 0,
    has_lifecycle_dates: false,
  };
  const empty = {
    subscribers_active_count: 0,
    subscribers_monthly_count: 0,
    subscribers_yearly_count: 0,
    subscriptions_inactive_count: 0,
    subscriptions_pricing_gaps_active: 0,
    mrr_eur: 0,
    arr_eur: 0,
    subscription_period: emptyPeriod,
  };

  const selectRich =
    "user_id, statut, current_period_end, plan, price_unit_amount_cents, activated_at, canceled_at";
  let { data: rows, error } = await supabase.from("subscriptions").select(selectRich).limit(8000);
  if (error && (isMissingColumnError(error, "activated_at") || isMissingColumnError(error, "plan"))) {
    const r2 = await supabase
    .from("subscriptions")
    .select("user_id, statut, current_period_end, plan, price_unit_amount_cents")
    .limit(8000);
    if (r2.error && isMissingColumnError(r2.error, "plan")) {
      const r3 = await supabase
      .from("subscriptions")
      .select("user_id, statut, current_period_end")
      .limit(8000);
      if (r3.error) {
        console.warn("[RdvMarket] admin/stats subscriptions:", r3.error.message);
      return empty;
    }
      rows = r3.data || [];
    } else if (r2.error) {
      console.warn("[RdvMarket] admin/stats subscriptions:", r2.error.message);
      return empty;
    } else {
    rows = r2.data || [];
    }
  } else if (error) {
    console.warn("[RdvMarket] admin/stats subscriptions:", error.message);
    return empty;
  }
  rows = rows || [];

  let nActive = 0;
  let nMonthly = 0;
  let nYearly = 0;
  let nInactive = 0;
  let mrr = 0;
  let nActiveNoPrice = 0;

  for (const row of rows || []) {
    if (isAdminSubscriptionRowActive(row)) {
      nActive += 1;
      const plan = String(row.plan || "").trim().toLowerCase();
      if (plan === "yearly") nYearly += 1;
      else if (plan === "monthly") nMonthly += 1;
      const me = subscriptionRowMrrEur(row);
      if (me == null) nActiveNoPrice += 1;
      else mrr += me;
    } else {
      nInactive += 1;
    }
  }

  const mrrRounded = round2(mrr);
  const subscriptionPeriod = buildAdminSubscriptionPeriodSeries(rows, dayKeys);
  return {
    subscribers_active_count: nActive,
    subscribers_monthly_count: nMonthly,
    subscribers_yearly_count: nYearly,
    subscriptions_inactive_count: nInactive,
    subscriptions_pricing_gaps_active: nActiveNoPrice,
    mrr_eur: mrrRounded,
    arr_eur: round2(mrrRounded * 12),
    subscription_period: subscriptionPeriod,
  };
}

function parseAdminOverviewDays(raw) {
  const n = Number(raw);
  if ([1, 7, 30, 90, 180, 365].includes(n)) return n;
  return 30;
}

function adminTxSaleAmountEur(t) {
  const v = t?.final_price ?? t?.amount ?? t?.montant ?? t?.prix;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Commission plateforme (€) sur une vente confirmée. */
function adminTxCommissionEur(t) {
  const c = Number(t?.commission);
  if (Number.isFinite(c) && c >= 0) return c;
  const price = Number(t?.price);
  if (Number.isFinite(price) && price > 0) return Math.round(price * 0.1 * 100) / 100;
  const sale = adminTxSaleAmountEur(t);
  return sale > 0 ? Math.round(sale * 0.1 * 100) / 100 : 0;
}

function adminEventMs(row, fields) {
  for (const f of fields) {
    const d = row?.[f];
    if (d) {
      const t = Date.parse(String(d));
      if (Number.isFinite(t)) return t;
    }
  }
  return null;
}

function adminDayKey(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

function adminBuildDayKeys(days) {
  const keys = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    keys.push(adminDayKey(d.getTime()));
  }
  return keys;
}

/** Métriques marketplace + séries journalières sur la période choisie (dashboard admin). */
async function aggregateAdminPeriodMetrics(days) {
  const since = new Date();
  since.setDate(since.getDate() - (days - 1));
  since.setHours(0, 0, 0, 0);
  const sinceMs = since.getTime();
  const sinceIso = since.toISOString();
  const dayKeys = adminBuildDayKeys(days);
  const buckets = Object.fromEntries(
    dayKeys.map((k) => [
      k,
      { date: k, sales: 0, gmv_eur: 0, commissions_eur: 0, users: 0, disputes: 0, listings: 0 },
    ]),
  );

  const [txRes, profRes, litRes, dosRes] = await Promise.all([
    supabase
      .from("transactions")
      .select(
        "id, statut, amount, montant, prix, final_price, commission, created_at, capture_confirmed_at, updated_at",
      )
      .gte("created_at", sinceIso)
      .limit(5000),
    supabase
      .from("profiles")
      .select("id, created_at, first_name, last_name, name, nom, email")
      .gte("created_at", sinceIso)
      .order("created_at", { ascending: false })
      .limit(800),
    supabase
      .from("litiges")
      .select("id, created_at, reason, status, statut")
      .gte("created_at", sinceIso)
      .order("created_at", { ascending: false })
      .limit(800),
    supabase
      .from("dossiers")
      .select("id, title, status, created_at")
      .gte("created_at", sinceIso)
      .order("created_at", { ascending: false })
      .limit(800),
  ]);

  let salesCount = 0;
  let gmvEur = 0;
  let commissionsEur = 0;
  const activity = [];

  for (const t of txRes.error ? [] : txRes.data || []) {
    const st = String(t.statut || "").toLowerCase();
    if (st !== "fonds_debloques") continue;
    const evt = adminEventMs(t, ["capture_confirmed_at", "updated_at", "created_at"]);
    if (evt == null || evt < sinceMs) continue;
    const gmv = adminTxSaleAmountEur(t);
    const comm = adminTxCommissionEur(t);
    salesCount += 1;
    gmvEur += gmv;
    commissionsEur += comm;
    const dk = adminDayKey(evt);
    if (buckets[dk]) {
      buckets[dk].sales += 1;
      buckets[dk].gmv_eur += gmv;
      buckets[dk].commissions_eur += comm;
    }
    activity.push({
      at: new Date(evt).toISOString(),
      type: "sale",
      entity_id: t.id,
      label: `Vente confirmée${gmv > 0 ? ` · ${gmv.toLocaleString("fr-FR")} €` : ""}${comm > 0 ? ` (commission ${comm.toLocaleString("fr-FR")} €)` : ""}`,
    });
  }

  let newUsers = 0;
  for (const p of profRes.error ? [] : profRes.data || []) {
    const evt = adminEventMs(p, ["created_at"]);
    if (evt == null || evt < sinceMs) continue;
    newUsers += 1;
    const dk = adminDayKey(evt);
    if (buckets[dk]) buckets[dk].users += 1;
    const name = profileDisplayName(p) || String(p.email || "").trim() || "Utilisateur";
    activity.push({
      at: new Date(evt).toISOString(),
      type: "user",
      entity_id: p.id,
      label: `Nouveau compte · ${name}`,
    });
  }

  let newDisputes = 0;
  for (const l of litRes.error ? [] : litRes.data || []) {
    const evt = adminEventMs(l, ["created_at"]);
    if (evt == null || evt < sinceMs) continue;
    newDisputes += 1;
    const dk = adminDayKey(evt);
    if (buckets[dk]) buckets[dk].disputes += 1;
    const motif = String(l.reason || "").trim();
    activity.push({
      at: new Date(evt).toISOString(),
      type: "dispute",
      entity_id: l.id,
      label: `Litige ouvert${motif ? ` · ${motif.slice(0, 72)}` : ""}`,
    });
  }

  let newListings = 0;
  for (const d of dosRes.error ? [] : dosRes.data || []) {
    const evt = adminEventMs(d, ["created_at"]);
    if (evt == null || evt < sinceMs) continue;
    const st = String(d.status || "").toLowerCase();
    if (st !== "online") continue;
    newListings += 1;
    const dk = adminDayKey(evt);
    if (buckets[dk]) buckets[dk].listings += 1;
    activity.push({
      at: new Date(evt).toISOString(),
      type: "listing",
      entity_id: d.id,
      label: `Annonce en ligne · ${String(d.title || "Sans titre").slice(0, 72)}`,
    });
  }

  activity.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  const round2 = (x) => Math.round(Number(x) * 100) / 100;
  return {
    days,
    since: sinceIso,
    sales_count: salesCount,
    gmv_eur: round2(gmvEur),
    commissions_eur: round2(commissionsEur),
    /** @deprecated utiliser gmv_eur */
    revenue_eur: round2(gmvEur),
    new_users: newUsers,
    new_disputes: newDisputes,
    new_listings_online: newListings,
    series: dayKeys.map((k) => ({
      ...buckets[k],
      gmv_eur: round2(buckets[k].gmv_eur || 0),
      commissions_eur: round2(buckets[k].commissions_eur || 0),
      revenue_eur: round2(buckets[k].gmv_eur || 0),
    })),
    recent_activity: activity.slice(0, 14),
  };
}

async function getAdminStats(params = {}) {
  await requireAdmin();
  const overviewDays = parseAdminOverviewDays(params?.days);

  // Dossiers online: nécessite `dossiers.status` (migration 003). Sinon, on renvoie 0 (car admin déjà en mode dégradé ailleurs).
  let dossiersOnline = 0;
  try {
    const { count, error } = await supabase
      .from("dossiers")
      .select("id", { count: "exact", head: true })
      .eq("status", "online");
    if (error) {
      if (!isMissingDossierColumnError(error, "status")) throw error;
    } else {
      dossiersOnline = Number(count || 0);
    }
  } catch {
    dossiersOnline = 0;
  }

  const [usersCount, disputesOpenCount, subFin, completedSales, period] = await Promise.all([
    adminStatsExactCount(supabase.from("profiles").select("id", { count: "exact", head: true })),
    countOpenLitigesForAdminStats(),
    aggregateAdminSubscriptionMetrics(overviewDays),
    adminStatsExactCount(
      supabase.from("transactions").select("id", { count: "exact", head: true }).eq("statut", "fonds_debloques"),
    ),
    aggregateAdminPeriodMetrics(overviewDays).catch((err) => {
      console.warn("[RdvMarket] admin/stats period:", err?.message || err);
      return null;
    }),
  ]);

  return {
    data: {
      users_count: Number(usersCount || 0),
      dossiers_online: Number(dossiersOnline || 0),
      dossiers_sold: Number(completedSales || 0),
      disputes_open: Number(disputesOpenCount || 0),
      gmv: period ? Number(period.gmv_eur || 0) : 0,
      platform_revenue: period ? Number(period.commissions_eur || 0) : 0,
      overview_days: overviewDays,
      period,
      ...subFin,
    },
  };
}

/** Compte supprimé par admin (soft delete) — repli si `deleted_at` absent du SELECT. */
export function isAdminProfileDeleted(row) {
  if (!row) return false;
  if (row.is_deleted || row.deleted_at) return true;
  return /^deleted\+[a-f0-9-]+@removed\.rdvmarket\.local$/i.test(String(row.email || "").trim());
}

async function getAdminUsers() {
  await requireAdmin();

  // Les colonnes varient selon les migrations réellement appliquées côté Supabase.
  // On tente un select "riche", et on retombe sur un select minimal si certaines colonnes n'existent pas.
  // Toujours inclure company_name dans les repli (siret n’existe pas encore en base → ne pas le mélanger au 1er essai).
  const tries = [
    "id, first_name, last_name, name, nom, email, role, kyc_status, banned, suspended_at, deleted_at, report_count, company_name",
    "id, name, nom, email, role, kyc_status, banned, suspended_at, deleted_at, company_name",
    "id, first_name, last_name, name, nom, email, role, kyc_status, banned, report_count, company_name",
    "id, first_name, last_name, name, nom, email, role, kyc_status, company_name",
    "id, name, nom, email, role, kyc_status, banned, report_count, company_name",
    "id, name, nom, email, role, kyc_status, company_name",
    "id, name, nom, email, role, company_name",
    "id, name, nom, email, role",
  ];

  let rows = null;
  for (const sel of tries) {
    const r = await supabase
      .from("profiles")
      .select(sel)
      .order("created_at", { ascending: false })
      .limit(800);
    if (!r.error) {
      rows = r.data || [];
      break;
    }
    // Si ce n'est pas une erreur de colonne manquante, on remonte.
    const msg = String(r.error?.message || "");
    const missingCol =
      ["banned", "suspended_at", "deleted_at", "report_count", "siret", "company_name", "kyc_status", "role", "created_at", "first_name", "last_name"].some(
        (c) => isMissingColumnError(r.error, c),
      );
    if (!missingCol) throw createHttpError(400, msg || "Erreur");
  }

  let safeRows = Array.isArray(rows) ? rows : [];
  const hasDeletedCol = safeRows.length > 0 && Object.prototype.hasOwnProperty.call(safeRows[0], "deleted_at");
  if (safeRows.length > 0 && !hasDeletedCol) {
    const ids = safeRows.map((p) => p.id).filter(Boolean);
    const { data: delPatch, error: delErr } = await supabase
      .from("profiles")
      .select("id, deleted_at")
      .in("id", ids);
    if (!delErr && Array.isArray(delPatch)) {
      const byId = Object.fromEntries(delPatch.map((r) => [r.id, r.deleted_at]));
      safeRows = safeRows.map((p) => ({ ...p, deleted_at: byId[p.id] ?? null }));
    }
  }

  const userIds = safeRows.map((p) => p.id).filter(Boolean);
  let subsByUserId = {};
  if (userIds.length > 0) {
    const subSelect = "user_id, statut, current_period_end, plan, canceled_at";
    let subRes = await supabase.from("subscriptions").select(subSelect).in("user_id", userIds);
    if (subRes.error && isMissingColumnError(subRes.error, "canceled_at")) {
      subRes = await supabase
        .from("subscriptions")
        .select("user_id, statut, current_period_end, plan")
        .in("user_id", userIds);
    }
    if (!subRes.error && Array.isArray(subRes.data)) {
      subsByUserId = Object.fromEntries(subRes.data.map((s) => [s.user_id, s]));
    }
  }

  return {
    data: safeRows.map((p) => {
      const deleted_at = p.deleted_at || null;
      const is_deleted = isAdminProfileDeleted({ ...p, deleted_at });
      const sub = subsByUserId[p.id] || null;
      const subscription_active = sub ? isSubscriptionRowPaidActive(sub) : false;
      return {
      user_id: p.id,
      name: profileDisplayName(p) || String(p.email || "").trim() || "(profil sans nom ni e-mail)",
      email: p.email || "",
      role: p.role || "buyer",
      kyc_status: p.kyc_status || "none",
      banned: Boolean(p.banned || false),
        suspended_at: p.suspended_at || null,
        deleted_at,
        is_deleted,
      report_count: Number(p.report_count || 0),
        siret: p.siret || null,
        company_name: p.company_name ? String(p.company_name).trim() : null,
        subscription_statut: sub?.statut || null,
        subscription_plan: sub?.plan || null,
        subscription_canceled_at: sub?.canceled_at || null,
        subscription_active,
      };
    }),
  };
}

function adminTransactionStatutLabelFr(st) {
  const s = String(st || "").trim().toLowerCase();
  const map = {
    paye: "Séquestre (payé)",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Appel effectué",
    fonds_debloques: "RDV confirmé · fonds débloqués",
    rembourse: "Remboursé",
    annule: "Annulé",
  };
  if (map[s]) return map[s];
  return s ? String(st).trim() : "—";
}

/** Détail admin : profil + transactions, annonces, litiges, négociations, abonnement, timeline d’activité. */
async function getAdminUserDetail(params) {
  await requireAdmin();
  const uid = String(params?.user_id || "").trim();
  if (!uid) throw createHttpError(400, "user_id requis");

  const { data: prof, error: pErr } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
  if (pErr) throw createHttpError(400, pErr.message);
  if (!prof) throw createHttpError(404, "Utilisateur introuvable");

  const phoneRaw = String(prof.telephone ?? prof.phone ?? "").trim() || null;

  const summary = {
    user_id: prof.id,
    name: profileDisplayName(prof) || String(prof.email || "").trim() || "(profil sans nom ni e-mail)",
    email: prof.email || "",
    phone: phoneRaw,
    role: prof.role || "buyer",
    kyc_status: prof.kyc_status || "none",
    banned: Boolean(prof.banned || false),
    suspended_at: prof.suspended_at || null,
    deleted_at: prof.deleted_at || null,
    is_suspended: Boolean(prof.suspended_at) && !prof.deleted_at,
    is_deleted: Boolean(prof.deleted_at),
    report_count: Number(prof.report_count || 0),
    siret: prof.siret || null,
    company_name: prof.company_name ? String(prof.company_name).trim() : null,
  };

  const [txRes, dosRes, negoRes, notRes, subRes] = await Promise.all([
    supabase
      .from("transactions")
      .select(
        "id, dossier_id, buyer_id, seller_id, price, buyer_total, commission, statut, created_at, updated_at",
      )
      .or(`buyer_id.eq.${uid},seller_id.eq.${uid}`)
      .order("created_at", { ascending: false })
      .limit(200),
    supabase
      .from("dossiers")
      .select("id, titre, status, type, price, created_at, updated_at, seller_id")
      .eq("seller_id", uid)
      .order("created_at", { ascending: false })
      .limit(200),
    supabase
      .from("negotiations")
      .select("id, dossier_id, buyer_id, seller_id, status, final_price, created_at")
      .or(`buyer_id.eq.${uid},seller_id.eq.${uid}`)
      .order("created_at", { ascending: false })
      .limit(120),
    supabase
      .from("notations")
      .select("id, stars, transaction_id, author_id, rated_user_id, created_at, comment")
      .or(`author_id.eq.${uid},rated_user_id.eq.${uid}`)
      .order("created_at", { ascending: false })
      .limit(80),
    supabase.from("subscriptions").select("*").eq("user_id", uid).maybeSingle(),
  ]);

  if (txRes.error) throw createHttpError(400, txRes.error.message);
  if (dosRes.error) throw createHttpError(400, dosRes.error.message);
  if (negoRes.error) throw createHttpError(400, negoRes.error.message);
  // notations / subscriptions : best-effort (schéma ou RLS variable)
  const notations = notRes.error ? [] : notRes.data || [];
  const subscription = subRes.error ? null : subRes.data || null;

  const transactions = txRes.data || [];
  const dossiers = dosRes.data || [];
  const negotiations = negoRes.data || [];

  const txById = Object.fromEntries(transactions.map((t) => [t.id, t]).filter(([k]) => Boolean(k)));
  const txDossierIds = [...new Set(transactions.map((t) => t.dossier_id).filter(Boolean))];
  let extraDossierRows = [];
  if (txDossierIds.length) {
    const dr = await supabase.from("dossiers").select("id, titre").in("id", txDossierIds);
    if (!dr.error && dr.data) extraDossierRows = dr.data;
  }
  const titreByDossierId = {};
  for (const d of [...dossiers, ...extraDossierRows]) {
    if (d?.id) {
      const t0 = String(d.titre || "").trim();
      titreByDossierId[d.id] = t0 || titreByDossierId[d.id] || "";
    }
  }

  const profileIdsNeeded = new Set();
  for (const t of transactions) {
    if (t.buyer_id) profileIdsNeeded.add(t.buyer_id);
    if (t.seller_id) profileIdsNeeded.add(t.seller_id);
  }
  for (const n of negotiations) {
    if (n.buyer_id) profileIdsNeeded.add(n.buyer_id);
    if (n.seller_id) profileIdsNeeded.add(n.seller_id);
  }
  for (const note of notations) {
    if (note.author_id) profileIdsNeeded.add(note.author_id);
    if (note.rated_user_id) profileIdsNeeded.add(note.rated_user_id);
  }
  const profIdList = [...profileIdsNeeded].slice(0, 160);
  let profById = {};
  if (profIdList.length) {
    const pr = await supabase.from("profiles").select("*").in("id", profIdList);
    if (!pr.error && pr.data) {
      profById = Object.fromEntries(
        (pr.data || []).map((p) => [
          p.id,
          profileDisplayName(p) || String(p.email || "").trim() || "Profil",
        ]),
      );
    }
  }

  const txIds = [...new Set(transactions.map((t) => t.id).filter(Boolean))];
  const [lOpened, lOnTx] = await Promise.all([
    supabase.from("litiges").select("*").eq("opened_by", uid).order("created_at", { ascending: false }).limit(100),
    txIds.length
      ? supabase.from("litiges").select("*").in("transaction_id", txIds).order("created_at", { ascending: false }).limit(150)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (lOpened.error) throw createHttpError(400, lOpened.error.message);
  if (lOnTx.error) throw createHttpError(400, lOnTx.error.message);

  const litigeByKey = new Map();
  for (const row of [...(lOpened.data || []), ...(lOnTx.data || [])]) {
    const k = String(row?.id ?? row?.dispute_id ?? "").trim();
    if (k && !litigeByKey.has(k)) litigeByKey.set(k, row);
  }
  const litiges = [...litigeByKey.values()].sort((a, b) => {
    const ta = Date.parse(String(a.created_at || "")) || 0;
    const tb = Date.parse(String(b.created_at || "")) || 0;
    return tb - ta;
  });

  const litigeOpenerIds = [...new Set(litiges.map((l) => l.opened_by).filter(Boolean))].filter((id) => !profById[id]);
  if (litigeOpenerIds.length) {
    const pr2 = await supabase.from("profiles").select("*").in("id", litigeOpenerIds.slice(0, 80));
    if (!pr2.error && pr2.data) {
      for (const p of pr2.data || []) {
        profById[p.id] = profileDisplayName(p) || String(p.email || "").trim() || "Profil";
      }
    }
  }

  const activity = [];
  const pushAct = (id, kind, at, title, subtitle = "", link = null) => {
    const t = at ? Date.parse(String(at)) : NaN;
    activity.push({
      id,
      kind,
      at: at || null,
      title,
      subtitle: subtitle || "",
      link_dossier_id: link?.dossier_id || null,
      link_litige_id: link?.litige_id || null,
      link_profile_id: link?.profile_id || null,
      _t: Number.isFinite(t) ? t : 0,
    });
  };

  if (prof.created_at) pushAct(`acc-${uid}`, "account", prof.created_at, "Compte créé", "");

  for (const d of dossiers) {
    if (d.created_at) {
      const titre = String(d.titre || "").trim() || "Annonce sans titre";
      pushAct(`dos-${d.id}-c`, "dossier", d.created_at, `Annonce · ${titre}`, dossierStatusLabelFr(d.status), { dossier_id: d.id });
    }
  }

  for (const t of transactions) {
    const role = t.buyer_id === uid ? "Acheteur" : "Vendeur";
    const prix = t.price ?? t.buyer_total;
    const montant =
      prix != null && prix !== "" && Number.isFinite(Number(prix)) ? `${Number(prix)} €` : null;
    const line = montant ? `${role} · ${montant}` : role;
    const peer = t.buyer_id === uid ? profById[t.seller_id] : profById[t.buyer_id];
    const ann = t.dossier_id ? titreByDossierId[t.dossier_id] : "";
    const subParts = [adminTransactionStatutLabelFr(t.statut)];
    if (peer) subParts.push(`avec ${peer}`);
    if (ann) subParts.push(`« ${ann} »`);
    const sub = subParts.join(" · ");
    if (t.created_at) {
      pushAct(`tx-${t.id}-c`, "transaction", t.created_at, `Transaction (${line})`, sub, t.dossier_id ? { dossier_id: t.dossier_id } : null);
    }
    if (t.updated_at && t.updated_at !== t.created_at) {
      pushAct(
        `tx-${t.id}-u`,
        "transaction_update",
        t.updated_at,
        `Transaction mise à jour (${line})`,
        sub,
        t.dossier_id ? { dossier_id: t.dossier_id } : null,
      );
    }
  }

  for (const n of negotiations) {
    const role = n.buyer_id === uid ? "Acheteur" : "Vendeur";
    if (n.created_at) {
      const fp = n.final_price != null && n.final_price !== "" ? ` · ${Number(n.final_price)} €` : "";
      const peer = n.buyer_id === uid ? profById[n.seller_id] : profById[n.buyer_id];
      const ann = n.dossier_id ? titreByDossierId[n.dossier_id] : "";
      const subParts = [negotiationStatusLabelFr(n.status)];
      if (peer) subParts.push(`avec ${peer}`);
      if (ann) subParts.push(`« ${ann} »`);
      pushAct(
        `neg-${n.id}`,
        "negotiation",
        n.created_at,
        `Négociation (${role}${fp})`,
        subParts.join(" · "),
        n.dossier_id ? { dossier_id: n.dossier_id } : null,
      );
    }
  }

  for (const l of litiges) {
    const litPk = String(l.id || l.dispute_id || "").trim();
    const txRow = l.transaction_id ? txById[l.transaction_id] : null;
    const annTitre = txRow?.dossier_id ? titreByDossierId[txRow.dossier_id] : "";
    const bL = txRow?.buyer_id ? profById[txRow.buyer_id] : null;
    const sL = txRow?.seller_id ? profById[txRow.seller_id] : null;
    const opener = l.opened_by ? profById[l.opened_by] : null;
    const parties = [bL && `Acheteur : ${bL}`, sL && `Vendeur : ${sL}`].filter(Boolean).join(" · ");
    const parts = [litigeRowStatusLabelFr(l.status ?? l.statut)];
    if (l.reason) parts.push(String(l.reason));
    if (opener) parts.push(`Ouvert par : ${opener}`);
    if (annTitre) parts.push(`Annonce : « ${annTitre} »`);
    if (parties) parts.push(parties);
    const linkL = litPk ? { litige_id: litPk } : null;
    if (l.created_at) {
      pushAct(`lit-${litPk}-o`, "litige_open", l.created_at, "Litige ouvert", parts.join(" · "), linkL);
    }
    if (l.resolved_at) {
      const partsR = ["Clôture enregistrée"];
      if (annTitre) partsR.push(`« ${annTitre} »`);
      if (parties) partsR.push(parties);
      pushAct(`lit-${litPk}-r`, "litige_resolve", l.resolved_at, "Litige clôturé", partsR.join(" · "), linkL);
    }
  }

  for (const n of notations) {
    if (!n.created_at) continue;
    const received = n.rated_user_id === uid;
    const stars = n.stars != null ? `${n.stars}/5` : "Note";
    const otherId = received ? n.author_id : n.rated_user_id;
    const other = otherId ? profById[otherId] : null;
    const subParts = [stars];
    if (other) subParts.push(received ? `De : ${other}` : `Vers : ${other}`);
    if (n.comment) subParts.push(String(n.comment).slice(0, 120));
    pushAct(
      `note-${n.id}`,
      "notation",
      n.created_at,
      received ? "Note reçue" : "Note laissée",
      subParts.join(" · "),
      otherId ? { profile_id: otherId } : null,
    );
  }

  activity.sort((a, b) => b._t - a._t);
  const activityOut = activity.map(({ _t, ...rest }) => rest);

  const transactionsOut = transactions.map((t) => {
    const peerId = t.buyer_id === uid ? t.seller_id : t.buyer_id;
    return {
      ...t,
      counterpart_user_id: peerId || null,
      counterpart_label: peerId ? profById[peerId] || null : null,
      dossier_title: t.dossier_id ? titreByDossierId[t.dossier_id] || null : null,
    };
  });

  const negotiationsOut = negotiations.map((n) => {
    const peerId = n.buyer_id === uid ? n.seller_id : n.buyer_id;
    return {
      ...n,
      counterpart_user_id: peerId || null,
      counterpart_label: peerId ? profById[peerId] || null : null,
      dossier_title: n.dossier_id ? titreByDossierId[n.dossier_id] || null : null,
    };
  });

  const litigesOut = litiges.map((l) => {
    const txRow = l.transaction_id ? txById[l.transaction_id] : null;
    return {
      ...l,
      buyer_label: txRow?.buyer_id ? profById[txRow.buyer_id] || null : null,
      seller_label: txRow?.seller_id ? profById[txRow.seller_id] || null : null,
      opened_by_label: l.opened_by ? profById[l.opened_by] || null : null,
      dossier_title: txRow?.dossier_id ? titreByDossierId[txRow.dossier_id] || null : null,
      dossier_id: txRow?.dossier_id || null,
    };
  });

  const notationsOut = notations.map((n) => ({
    ...n,
    author_label: n.author_id ? profById[n.author_id] || null : null,
    rated_user_label: n.rated_user_id ? profById[n.rated_user_id] || null : null,
  }));

  return {
    data: {
      summary,
      profile: prof,
      transactions: transactionsOut,
      dossiers,
      litiges: litigesOut,
      negotiations: negotiationsOut,
      notations: notationsOut,
      subscription,
      activity: activityOut,
    },
  };
}

async function postAdminUserBanToggle(userId, banned) {
  await requireAdmin();
  if (!userId) throw createHttpError(400, "user_id requis");

  const patch = { banned: Boolean(banned) };
  if (!banned) patch.report_count = 0;

  const { error } = await supabase.from("profiles").update(patch).eq("id", userId);
  if (error) {
    if (isMissingColumnError(error, "banned")) {
      throw createHttpError(
        400,
        "Colonne `profiles.banned` manquante : exécutez `db/migrations/067_profiles_account_moderation.sql` sur Supabase.",
      );
    }
    if (!banned && isMissingColumnError(error, "report_count")) {
      const { error: e2 } = await supabase.from("profiles").update({ banned: false }).eq("id", userId);
      if (e2) throw createHttpError(400, e2.message);
      return { data: { ok: true } };
    }
    throw createHttpError(400, error.message);
  }
  return { data: { ok: true } };
}

async function withdrawSellerOnlineDossiersClient(sellerId) {
  const { error } = await supabase
    .from("dossiers")
    .update({ status: "withdrawn" })
    .eq("seller_id", sellerId)
    .eq("status", "online");
  if (error) console.warn("[RdvMarket] withdraw dossiers:", error.message);
}

/** Suspendre, réactiver ou supprimer un compte (API serveur si dispo, sinon repli client pour suspend). */
async function postAdminUserAccount(body = {}) {
  await requireAdmin();
  const user_id = String(body.user_id || body.userId || "").trim();
  const action = String(body.action || "").trim().toLowerCase();
  if (!user_id) throw createHttpError(400, "user_id requis");
  if (!["suspend", "unsuspend", "delete"].includes(action)) {
    throw createHttpError(400, "action invalide");
  }

  try {
    return await nextFetch("POST", "/admin/user-account", { user_id, action });
  } catch (err) {
    const st = err?.response?.status;
    if (st && st !== 404) throw err;
  }

  if (action === "delete") {
    throw createHttpError(
      503,
      "La suppression de compte nécessite l’API serveur (déploiement Vercel avec SUPABASE_SERVICE_ROLE_KEY).",
    );
  }

  const { data: target, error: tErr } = await supabase
    .from("profiles")
    .select("role, deleted_at")
    .eq("id", user_id)
    .maybeSingle();
  if (tErr) throw createHttpError(400, tErr.message);
  if (!target) throw createHttpError(404, "Utilisateur introuvable");
  if (target.role === "admin") throw createHttpError(400, "Impossible de modérer un compte administrateur");

  const now = new Date().toISOString();
  if (action === "suspend") {
    const { error } = await supabase
      .from("profiles")
      .update({ suspended_at: now, banned: true, deleted_at: null })
      .eq("id", user_id);
    if (error) {
      if (isMissingColumnError(error, "suspended_at")) {
        throw createHttpError(
          400,
          "Colonne `profiles.suspended_at` manquante : exécutez la migration 067 sur Supabase.",
        );
      }
      throw createHttpError(400, error.message);
    }
    await withdrawSellerOnlineDossiersClient(user_id);
    return { data: { ok: true, action: "suspend" } };
  }

  const { error } = await supabase
    .from("profiles")
    .update({ suspended_at: null, banned: false })
    .eq("id", user_id)
    .is("deleted_at", null);
  if (error) throw createHttpError(400, error.message);
  return { data: { ok: true, action: "unsuspend" } };
}

async function postAdminUserKycDecision(userId, decision) {
  await requireAdmin();
  if (!userId) throw createHttpError(400, "user_id requis");
  if (decision !== "approve" && decision !== "reject") throw createHttpError(400, "decision invalide");

  const next = decision === "approve" ? "approved" : "rejected";
  const { error } = await supabase.from("profiles").update({ kyc_status: next }).eq("id", userId);
  if (error) {
    if (isMissingColumnError(error, "kyc_status")) {
      throw createHttpError(
        400,
        "Colonne `profiles.kyc_status` manquante : exécutez `db/migrations/005_profiles_colonnes_inscription.sql` sur Supabase, puis réessayez.",
      );
    }
    throw createHttpError(400, error.message);
  }
  return { data: { ok: true } };
}

async function postReports(body) {
  const user = await requireUser();
  const { error } = await supabase.from("signalements").insert({
    ...body,
    reporter_id: user.id,
  });
  if (error) throw createHttpError(400, error.message);
  return { data: { ok: true } };
}

async function getCallsStatus(dossierId) {
  const { data, error } = await supabase
    .from("call_sessions")
    .select("id,status")
    .eq("dossier_id", dossierId)
    .in("status", ["ringing", "active", "in_progress"])
    .limit(1);
  if (error) return { data: { call_active: false } };
  return { data: { call_active: (data || []).length > 0 } };
}

async function getUserProfile(userId) {
  const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
  if (error) throw createHttpError(400, error.message);
  if (!data) throw createHttpError(404, "Utilisateur introuvable");
  const { data: authData } = await runAuthLocked(() => supabase.auth.getUser());
  const viewerId = authData?.user?.id;
  const email = viewerId === userId ? authData.user.email : data.email;
  const row = { ...data };
  if (viewerId !== userId) {
    delete row.last_seen_ip;
    delete row.last_seen_ip_at;
  }
  return {
    data: {
      ...row,
      user_id: row.id,
      email: email || row.email,
      rating_avg: row.rating_avg ?? row.avg_rating,
      rating_count: row.rating_count ?? row.notes_count ?? 0,
    },
  };
}

async function getUserRatings(userId) {
  const { data, error } = await supabase
    .from("notations")
    .select("*")
    .eq("rated_user_id", userId)
    .order("created_at", { ascending: false });
  if (error) {
    const { data: d2, error: e2 } = await supabase.from("notations").select("*").eq("profile_id", userId);
    if (e2) throw createHttpError(400, e2.message);
    return { data: d2 || [] };
  }
  return { data: data || [] };
}

/** Colonne texte du libellé (beaucoup de BDD : `nom_champ` NOT NULL, l’app envoie `nom` — ordre important) */
const PRODUIT_CHAMP_LABEL_KEYS = ["nom_champ", "nom", "name", "libelle", "label", "titre", "code"];
/** Où stocker text|number|list|boolean (mot « type » réservé côté SQL/PostgREST) */
const PRODUIT_CHAMP_TYPE_KEYS = ["type", "champ_type", "field_type"];

function produitChampLabelText(c) {
  if (!c) return "";
  return String(c.nom ?? c.nom_champ ?? c.name ?? c.libelle ?? c.label ?? c.titre ?? c.code ?? "").trim();
}

/**
 * @param {object} mode
 * @param {string|null} mode.requiredKey — clé booléen « obligatoire » (null = ne pas envoyer)
 * @param {string|null} typeKey — clé de la nature du champ (null = ne pas envoyer)
 */
function buildProduitChampInsertRows(champs, produitId, labelKey, typeKey, mode) {
  const m = {
    withOptions: true,
    withOrdre: true,
    withVisible: true,
    withType: true,
    withCible: true,
    requiredKey: "required",
    ...mode,
  };
  return champs.map((c, index) => {
    const label = produitChampLabelText(c) || c.nom;
    const row = { [labelKey]: label, produit_id: produitId };
    if (m.withType && typeKey) {
      row[typeKey] = c.type;
    }
    if (m.withOptions) row.options = c.options || [];
    if (m.withOrdre) row.ordre = index;
    if (m.withVisible) row.visible_public = c.visible_public !== false;
    if (m.withCible !== false) {
      const ca = c.cible_annonces;
      row.cible_annonces = ca === "vente" || ca === "recherche" ? ca : "tous";
    }
    if (m.requiredKey) {
      row[m.requiredKey] = Boolean(c.required);
    }
    return row;
  });
}

/** Jeux de colonnes du plus riche au minimal (schémas Supabase hétérogènes). */
const PRODUIT_CHAMP_INSERT_MODES_BASE = [
  { withOptions: true, withOrdre: true, withVisible: true, requiredKey: "required" },
  { withOptions: true, withOrdre: true, withVisible: true, requiredKey: "is_required" },
  { withOptions: true, withOrdre: true, withVisible: true, requiredKey: "obligatoire" },
  { withOptions: true, withOrdre: true, withVisible: true, requiredKey: null },
  { withOptions: false, withOrdre: true, withVisible: true, requiredKey: null },
  { withOptions: true, withOrdre: true, withVisible: false, requiredKey: null },
  { withOptions: false, withOrdre: true, withVisible: false, requiredKey: null },
  { withOptions: true, withOrdre: false, withVisible: false, requiredKey: null },
  { withOptions: false, withOrdre: false, withVisible: false, requiredKey: null },
  { withType: false, withOptions: true, withOrdre: true, withVisible: true, requiredKey: null },
  { withType: false, withOptions: true, withOrdre: true, withVisible: false, requiredKey: null },
  { withType: false, withOptions: false, withOrdre: true, withVisible: false, requiredKey: null },
];
const PRODUIT_CHAMP_INSERT_MODES = [
  ...PRODUIT_CHAMP_INSERT_MODES_BASE.map((b) => ({ ...b, withCible: true })),
  ...PRODUIT_CHAMP_INSERT_MODES_BASE.map((b) => ({ ...b, withCible: false })),
];

function toProduitChampRows(champs, produitId) {
  return buildProduitChampInsertRows(champs, produitId, "nom", "type", PRODUIT_CHAMP_INSERT_MODES[0]);
}

function isProduitChampSchemaError(msg) {
  const m = (msg || "").toLowerCase();
  return (
    m.includes("visible_public") ||
    m.includes("cible_annonces") ||
    m.includes("ordre") ||
    m.includes("required") ||
    m.includes("options") ||
    m.includes("type") ||
    m.includes("nom_champ") ||
    m.includes("could not find") ||
    (m.includes("column") && (m.includes("does not exist") || m.includes("schema cache"))) ||
    m.includes("schema cache") ||
    // ex. seulement `nom` rempli et NOT NULL sur `nom_champ` : retenter d’autres noms de colonne
    (m.includes("not-null") && m.includes("nom") && !m.includes("produit_id"))
  );
}

/** Erreur sur laquelle on peut tenter un autre nom de colonne ou moins de champs */
function isProduitChampInsertRetryable(msg) {
  const m = (msg || "").toLowerCase();
  if (m.includes("row-level") || m.includes("policy") || m.includes("rls") || m.includes("foreign key")) {
    return false;
  }
  return isProduitChampSchemaError(m);
}

/**
 * Bases hétérogènes (libellé, clé "type" vs champ_type, required, options, etc.) : essais en cascade.
 */
async function insertProduitChampRowsTolerant(produitId, champs) {
  if (!Array.isArray(champs) || !champs.length) return;
  champs = champs.filter((c) => produitChampLabelText(c).length > 0);
  if (!champs.length) return;
  for (const labelKey of PRODUIT_CHAMP_LABEL_KEYS) {
    for (const typeKey of PRODUIT_CHAMP_TYPE_KEYS) {
      for (const mode of PRODUIT_CHAMP_INSERT_MODES) {
        const rows = buildProduitChampInsertRows(champs, produitId, labelKey, typeKey, mode);
        if (!rows.length) return;
        const { error: ce } = await supabase.from("produit_champs").insert(rows);
        if (!ce) return;
        const lastMsg = ce.message || "";
        if (!isProduitChampInsertRetryable(lastMsg)) {
          throw createHttpError(400, lastMsg);
        }
      }
    }
  }
  throw createHttpError(
    400,
    "Enregistrement des champs produit impossible. Migrations 011 / 012 (produit_champs) sur Supabase, puis notify pgrst, 'reload schema'."
  );
}

async function crudProduit(method, id, body) {
  if (method === "PUT" || method === "POST" || method === "DELETE") {
    await requireAdmin();
  }
  if (method === "PUT" || method === "POST") {
    if (texteContientCoordonnees(body?.description)) {
      throw createHttpError(400, messageCoordonneesInterdites);
    }
  }
  if (method === "DELETE") {
    const { error } = await supabase.from("produits").update({ actif: false }).eq("id", id);
    if (error) throw createHttpError(400, error.message);
    return { data: { ok: true } };
  }
  if (method === "PUT") {
    const pid = String(id || "").trim();
    if (!pid) throw createHttpError(400, "produit_id requis");
    const { champs, ...rest } = body || {};
    const produitPatch = {};
    if (rest.nom != null) produitPatch.nom = String(rest.nom).trim();
    if (rest.description != null) produitPatch.description = rest.description;
    if (rest.secteur != null) produitPatch.secteur = rest.secteur;
    if (Object.keys(produitPatch).length) {
      const { error } = await supabase.from("produits").update(produitPatch).eq("id", pid);
    if (error) throw createHttpError(400, error.message);
    }
    if (Array.isArray(champs)) {
      const prepared = prepareChampsForSave(champs);
      const withLabel = (champs || []).filter((c) => produitChampLabelText(c).length > 0);
      if (withLabel.length > 0 && prepared.length === 0) {
        throw createHttpError(400, "Impossible d’enregistrer les champs : libellé manquant ou invalide.");
      }
      const { error: delErr } = await supabase.from("produit_champs").delete().eq("produit_id", pid);
      if (delErr) throw createHttpError(400, delErr.message);
      if (prepared.length) {
        await insertProduitChampRowsTolerant(pid, prepared);
      }
    }
    const refreshed = await getProduitOne(pid);
    return { data: refreshed };
  }
  if (method === "POST") {
    const { champs, ...rest } = body || {};
    const insertRow = {
      nom: String(rest.nom || "").trim(),
      description: rest.description ?? "",
      secteur: rest.secteur,
    };
    const { data: created, error } = await supabase.from("produits").insert(insertRow).select().single();
    if (error) throw createHttpError(400, error.message);
    const newId = created.id;
    const prepared = prepareChampsForSave(champs);
    if (prepared.length) {
      await insertProduitChampRowsTolerant(newId, prepared);
    }
    const refreshed = await getProduitOne(newId);
    return { data: refreshed };
  }
  throw createHttpError(400, "Méthode invalide");
}

async function crudSecteur(method, id, body) {
  if (method === "DELETE") {
    const { error } = await supabase.from("secteurs").update({ actif: false }).eq("id", id);
    if (error) throw createHttpError(400, error.message);
    return { data: { ok: true } };
  }
  if (method === "PUT") {
    const { error } = await supabase.from("secteurs").update(body).eq("id", id);
    if (error) throw createHttpError(400, error.message);
    return { data: { ok: true } };
  }
  if (method === "POST") {
    const { data, error } = await supabase.from("secteurs").insert(body).select().single();
    if (error) throw createHttpError(400, error.message);
    return { data };
  }
  throw createHttpError(400, "Méthode invalide");
}

async function dispatch(method, urlPath, data, config = {}) {
  const path = urlPath.replace(/\/{2,}/g, "/");
  const params = config.params || {};

  if (path === "/dossiers" && method === "GET") return getDossiersList(params);
  if (path === "/dossiers/mine" && method === "GET") return getDossiersMine();
  if (path === "/produits" && method === "GET") return getProduits();
  if (path === "/produits" && method === "POST") return crudProduit("POST", null, data);

  const mDossier = path.match(/^\/dossiers\/([^/]+)$/);
  if (mDossier && method === "GET") return getDossierOne(mDossier[1]);
  if (mDossier && method === "PUT") return updateDossierMine(mDossier[1], data);
  if (mDossier && method === "DELETE") return deleteDossierMine(mDossier[1]);

  if (path === "/dossiers" && method === "POST") return postDossier(data);

  const dm = path.match(/^\/dossiers\/([^/]+)\/moderate$/);
  if (dm && method === "PUT") return nextFetch("PUT", path, null, config);

  const dneg = path.match(/^\/dossiers\/([^/]+)\/negotiations\/mine$/);
  if (dneg && method === "GET") return getDossierNegotiationMine(dneg[1]);

  const dnego = path.match(/^\/dossiers\/([^/]+)\/negotiations$/);
  if (dnego && method === "POST") return postDossierNegotiation(dnego[1], data);

  const dmsg = path.match(/^\/dossiers\/([^/]+)\/messages$/);
  if (dmsg && method === "GET") return getDossierMessages(dmsg[1]);
  if (dmsg && method === "POST") return postDossierMessage(dmsg[1], data);

  const dbuy = path.match(/^\/dossiers\/([^/]+)\/buy$/);
  if (dbuy && method === "POST") return postDossierBuy(dbuy[1], params);

  const pProd = path.match(/^\/produits\/([^/]+)$/);
  if (pProd && method === "GET") return getProduitOne(pProd[1]);
  if (pProd && method === "PUT") return crudProduit("PUT", pProd[1], data);
  if (pProd && method === "DELETE") return crudProduit("DELETE", pProd[1], null);

  const pSec = path.match(/^\/secteurs\/([^/]+)$/);
  if (pSec && method === "PUT") return crudSecteur("PUT", pSec[1], data);
  if (pSec && method === "DELETE") return crudSecteur("DELETE", pSec[1], null);

  if (path === "/secteurs" && method === "GET") {
    const { data: rows, error } = await supabase.from("secteurs").select("*").order("nom");
    if (error) return nextFetch("GET", path, null, config);
    return {
      data: (rows || []).map((s) => ({
        ...s,
        secteur_id: s.secteur_id ?? s.id,
      })),
    };
  }
  if (path === "/secteurs" && method === "POST") return crudSecteur("POST", null, data);

  if (path === "/kyc/verify" && method === "POST") return annuaireVerify(data.country, data.registration_number);
  if (path === "/kyc/submit" && method === "POST") return kycSubmit(data);

  if (path === "/transactions/mine" && method === "GET") {
    try {
      return await nextFetch("GET", path, null, config);
    } catch (err) {
      const st = err?.response?.status;
      // En dev (CRA seul) : /api/… peut être absent — repli sur Supabase côté navigateur.
      if (st === 404) return getTransactionsMine(params);
      throw err;
    }
  }

  if (path === "/negotiations/received" && method === "GET") return getNegotiationsReceived();
  if (path === "/negotiations/mine" && method === "GET") return getNegotiationsMine();

  const nOne = path.match(/^\/negotiations\/([^/]+)$/);
  if (nOne && method === "DELETE") return deleteNegotiationMine(nOne[1]);

  const nOff = path.match(/^\/negotiations\/([^/]+)\/offers$/);
  if (nOff && method === "POST") return postNegotiationOffer(nOff[1], data);

  const nResp = path.match(/^\/negotiations\/([^/]+)\/respond$/);
  if (nResp && method === "POST") return postNegotiationRespond(nResp[1], params);

  if (path === "/ratings" && method === "POST") return postRatings(data);

  if (path === "/disputes/mine" && method === "GET") {
    try {
      return await nextFetch("GET", path, null, config);
    } catch (err) {
      if (err?.response?.status === 404) return getDisputesMine();
      throw err;
    }
  }
  if (path === "/disputes" && method === "POST") {
    try {
      return await nextFetch("POST", path, data, config);
    } catch (err) {
      if (err?.response?.status === 404) return postDisputes(data);
      throw err;
    }
  }
  if (path === "/reports" && method === "POST") return postReports(data);

  const cst = path.match(/^\/calls\/status\/([^/]+)$/);
  if (cst && method === "GET") return getCallsStatus(cst[1]);

  const usr = path.match(/^\/users\/([^/]+)$/);
  if (usr && method === "GET" && !path.includes("/ratings")) return getUserProfile(usr[1]);

  const ura = path.match(/^\/users\/([^/]+)\/ratings$/);
  if (ura && method === "GET") return getUserRatings(ura[1]);

  if (path === "/admin/dossiers/pending" && method === "GET") return getAdminDossiersByStatus("pending");
  if (path === "/admin/dossiers/online" && method === "GET") return getAdminDossiersByStatus("online");
  if (path === "/admin/dossiers/hidden" && method === "GET") return getAdminDossiersHidden();
  if (path === "/admin/catalog-broadcast" && method === "GET") {
    try {
      return await nextFetch("GET", path, null, { ...config, params: { ...params, ...(config.params || {}) } });
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      return getAdminCatalogBroadcast(params);
    }
  }
  if (path === "/admin/whatsapp-digest" && method === "GET") {
    try {
      return await nextFetch("GET", path, null, { ...config, params: { ...params, ...(config.params || {}) } });
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
    }
    return getAdminCatalogBroadcast(params);
  }
  if (path === "/admin/disputes" && method === "GET") {
    try {
      return await nextFetch("GET", path, null, config);
    } catch (err) {
      if (err?.response?.status === 404) return getAdminDisputesEnriched();
      throw err;
    }
  }
  if (path === "/admin/stats" && method === "GET") return getAdminStats(params);
  if (path === "/admin/user-account" && method === "POST") return postAdminUserAccount(data);
  if (path === "/admin/users" && method === "GET") return getAdminUsers();
  if (path === "/admin/user-detail" && method === "GET") return getAdminUserDetail(params);
  const mAdUserAction = path.match(/^\/admin\/users\/([^/]+)\/(ban|unban|kyc)$/);
  if (mAdUserAction && method === "POST") {
    const userId = mAdUserAction[1];
    const action = mAdUserAction[2];
    if (action === "ban") return postAdminUserBanToggle(userId, true);
    if (action === "unban") return postAdminUserBanToggle(userId, false);
    // kyc
    const decision = String(config?.params?.decision || "").trim();
    return postAdminUserKycDecision(userId, decision);
  }
  const mAdW = path.match(/^\/admin\/dossiers\/([^/]+)\/withdraw$/);
  if (mAdW && method === "PUT") return putAdminWithdrawDossier(mAdW[1]);
  const mAdPub = path.match(/^\/admin\/dossiers\/([^/]+)\/publish$/);
  if (mAdPub && method === "PUT") return putAdminPublishDossier(mAdPub[1]);
  const mAdDel = path.match(/^\/admin\/dossiers\/([^/]+)\/delete$/);
  if (mAdDel && method === "DELETE") return deleteAdminDossier(mAdDel[1]);
  const mAdDossierPut = path.match(/^\/admin\/dossiers\/([^/]+)$/);
  if (mAdDossierPut && method === "PUT") return putAdminUpdateDossier(mAdDossierPut[1], data);
  const mAdR = path.match(/^\/admin\/disputes\/([^/]+)\/resolve$/);
  if (mAdR && method === "POST") {
    const merged = {
      ...params,
      ...(data && typeof data === "object" && !Array.isArray(data) ? data : {}),
    };
    return postAdminDisputeResolve(mAdR[1], merged);
  }

  if (path.startsWith("/admin/") || path === "/kyc/upload") return nextFetch(method, path, data, config);

  if (path === "/subscriptions/me" && method === "GET") return getMySubscription(config);
  if (path === "/connect/status" && method === "GET") return getConnectStatus();
  if (path === "/connect/balance" && method === "GET") return getConnectBalance();
  if (path === "/connect/start" && method === "POST") return postConnectStart(data);
  if (path === "/connect/dashboard" && method === "POST") return postConnectDashboard();

  if (
    path === "/recherche-proposals/mine" ||
    path === "/recherche-proposals/batch" ||
    path === "/recherche-proposals/respond"
  ) {
    return nextFetch(method, path, data, config);
  }

  if (
    path.startsWith("/calls/") ||
    path.startsWith("/call/") ||
    path.startsWith("/invoices/") ||
    path === "/auth/session"
  ) {
    return nextFetch(method, path, data, config);
  }

  if (path.startsWith("/auth/")) return nextFetch(method, path, data, config);

  return nextFetch(method, path, data, config);
}

function wrap(p) {
  return p.catch((err) => {
    if (!err.response) throw createHttpError(0, err.message || "Erreur réseau");
    throw err;
  });
}

/** Litige admin : audio avec Bearer (un simple lien <a href=/api/...> n’envoie pas le jeton). */
export async function fetchAdminCallRecording(transactionId, { callSessionId } = {}) {
  const tid = String(transactionId || "").trim();
  const sid = String(callSessionId || "").trim();
  if (!tid && !sid) throw createHttpError(400, "transactionId ou callSessionId requis");
  return nextFetch("GET", "/admin/call-recording", null, {
    params: {
      ...(tid ? { transactionId: tid } : {}),
      ...(sid ? { callSessionId: sid } : {}),
    },
    responseType: "blob",
  });
}

export const api = {
  get: (path, config) => wrap(dispatch("GET", path, null, config)),
  post: (path, data, config) => wrap(dispatch("POST", path, data, config || {})),
  put: (path, data, config) => wrap(dispatch("PUT", path, data, config || {})),
  delete: (path, config) => wrap(dispatch("DELETE", path, null, config || {})),
  patch: (path, data, config) => wrap(dispatch("PATCH", path, data, config || {})),
  defaults: { headers: {} },
  interceptors: {
    request: { use: () => 0 },
    response: { use: () => 0 },
  },
};
